import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { queueExpense } from "../../utils/offlineExpenses";
import { useAuth } from "../../App";
import { convertPdfToJpeg } from "../../utils/pdfConverter";
import { celebrate } from "../../components/CelebrationHub";
import { downloadTripExpenseExcel } from "../../utils/tripExpenseExcel";
import {
  getBillTypeForCategory,
  getExpenseCategoriesForTrip,
  isOetSpecialCategory,
  isOetTrip as isOetTripForRules,
  OET_SPECIAL_CATEGORIES,
  PERSON_DETAIL_CATEGORIES,
  getOetCashVoucherApprovalLimit,
} from "./oetExpenseModule";
import {
  buildNameFields,
  getFuelFieldValidationMessage,
  isCentralizedTrainBooking,
  isCompanyPaidHotelBlocked,
} from "./oetExpenseEntryRules";

const CATEGORIES = ["Travel","Hotel","Food","Fuel","Courier","Delivery Charges","Stationeries","Prints","Miscellaneous","Tips"];

// Strict production WAF rules reject punctuation such as apostrophes in a
// multipart filename before the request can reach Express. Keep the original
// file bytes/type, but transmit a conservative filename that is safe across
// browsers, proxies and ModSecurity.
const safeUploadFile = (file) => {
  if (!file || typeof file.name !== "string") return file;
  const safeName = file.name
    .normalize("NFKD")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^\.+/, "")
    .replace(/_+/g, "_")
    .slice(0, 120) || "upload";
  if (safeName === file.name) return file;
  return new File([file], safeName, { type: file.type, lastModified: file.lastModified });
};

// Extra categories available only for Ebekmed employees
const EBEKMED_EXTRA_CATEGORIES = ["Medical Equipment", "Medical Consumables"];
const SUBCATEGORY_REQUIRED_CATEGORIES = ["Medical Equipment", "Medical Consumables", "Delivery Charges"];

// Additional categories shown when the trip is "Petty Cash" (any company)
const PETTY_CASH_CATEGORIES = [
  "Maid Salary",
  "House Keeping Expenses",
  "Drinking Water",
  "Office Maintenance Expenses",
  "Gifts Expenses",
  "Brand Prints / Ironing / Washing",
  "Office Asset Maintenance",
  "Office Consumables",
];

// Sub-type options for each Ebekmed category
const EBEKMED_SUBCATEGORY_OPTIONS = {
  "Medical Equipment":   ["Training Requirement", "Missed to Purchase", "Forgot to Order / Bring", "Additional Usage"],
  "Medical Consumables": ["Missed to Purchase", "Forgot to Order / Bring", "Additional Usage"],
  "Delivery Charges":    ["Certificates", "Consumables", "Brochures", "Others"],
};

const CATEGORY_ICONS = {
  Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌",
  "Interlocutor Fee":"🗣️", "Interlocutor Conveyance":"🚕", "Helper Charges":"🤝", "Support Staff":"🦺",
  "Invigilator Fee":"📋", "Catering Charges":"🍱", Tips:"💵",
  "Medical Equipment":"🔧", "Medical Consumables":"💊", "Delivery Charges":"🚚",
  // Petty Cash categories
  "Maid Salary":"🧹", "House Keeping Expenses":"🏠", "Drinking Water":"💧",
  "Office Maintenance Expenses":"🔨", "Gifts Expenses":"🎁", "Brand Prints / Ironing / Washing":"👕",
  "Office Asset Maintenance":"🖥️", "Office Consumables":"📦",
};

// Cash voucher sub-types per trip category (Ebek only)
const CAMBRIDGE_CASH_VOUCHER_TYPES = [
  "Presenters Travel Expenses",
];

const TRAVEL_SUBTYPES    = ["Train", "Bus", "Flight", "Cab", "Auto", "Bike Taxi", "Others"];
const FUEL_VEHICLE_TYPES = ["Personal Vehicle", "Hired Vehicle"];
const FUEL_TYPES         = ["Petrol", "Diesel", "CNG"];
const COURIER_SUBTYPES   = ["Within City", "Other State", "Other Country"];
const FOOD_SUBCATEGORIES = ["BF","Lunch","Dinner","Snacks","Tea/Coffee/Juice","Other"];
const PRINT_SUBCATEGORIES = ["Exam Materials", "Certificates", "Company Brochures", "Others"];

const BF_OVERRIDE_REASONS = [
  "Food Not ready",
  "Started Early for exam",
  "Reduced BF cost from hotel bill",
  "Others",
];

const FOOD_EXTERNAL_GROUP_TYPES = [
  "Client / Customer", "Institution Staff", "Trainer / Faculty", "Freelancer",
  "Interlocutor / Invigilator", "Support Staff", "Vendor / Driver", "Other",
];

const appendFoodSharingFields = (fd, form) => {
  if (form.shared_with_ids?.length) fd.append("shared_with_ids", JSON.stringify(form.shared_with_ids));
  const groups = Object.entries(form.food_external_groups || {})
    .map(([type, count]) => ({ type, count: Number(count) }))
    .filter(item => item.count > 0);
  if (groups.length) fd.append("food_external_groups", JSON.stringify(groups));
  if (form.food_pack_count !== "") fd.append("food_pack_count", form.food_pack_count);
};

// MySQL DATE values can arrive as an ISO timestamp representing midnight in
// the server timezone. Taking the first ten characters can therefore show the
// previous day in India. Keep plain DATE strings unchanged and convert actual
// timestamps to the browser's local calendar date.
const toLocalDateInputValue = (value = new Date()) => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = number => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const DEFAULT_FORM = {
  expense_date: toLocalDateInputValue(),
  category: "Travel",
  food_subcategory: "",
  hotel_name: "",
  hotel_location: "",
  breakfast_included: false,
  hotel_company_paid: false,
  bf_override_reason: "",
  description: "",
  amount: "",
  bill_type: "bill",
  bill: null,
  cash_voucher_type: "",
  // Sub-type fields
  travel_subcategory: "",
  travel_booked_by: "",
  travel_from: "",
  travel_to: "",
  travel_class: "",
  cab_type: "",
  cab_charge_type: "",
  cab_kms_travelled: "",
  cab_total_hours: "",
  cab_institutions_visited: "",
  fuel_vehicle_type: "",
  fuel_type: "",
  fuel_vehicle_no: "",
  fuel_liters: "",
  kms_travelled: "",
  odometer_start: "",
  odometer_end: "",
  odometer_start_photo: null,
  odometer_end_photo: null,
  courier_subtype: "",
  courier_name: "",
  courier_weight: "",
  courier_from: "",
  courier_to: "",
  courier_boxes: "",
  courier_tracking_ref: "",
  print_subcategory: "",
  misc_category: "",
  expense_subcategory: "",  // For Medical Equipment, Medical Consumables, Delivery Charges
  multiple_stops: false,
  extra_stops: [],
  oet_interlocutor_count: "",
  oet_interlocutor_names: [],
  oet_freelancer_count: "",
  oet_freelancer_names: [],
  oet_support_staff_count: "",
  oet_support_staff_names: [],
  catering_candidate_count: "",
  catering_ebek_staff_count: "",
  catering_support_staff_count: "",
  catering_bf_packs: "",
  catering_lunch_packs: "",
  catering_snacks_packs: "",
  // Shared expense
  is_shared: false,
  shared_with_ids: [],
  food_external_groups: {},
  food_pack_count: "",
};

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const expenseModalRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [billPreview, setBillPreview] = useState(null);
  // Deferred OET declaration state
  const [declForm, setDeclForm] = useState({
    oet_candidate_count:"", oet_interlocutor_count:"", oet_interlocutor_names:"",
    oet_freelancer_count:"", oet_freelancer_names:"",
    oet_support_staff_count:"", oet_support_staff_names:"",
  });
  const [declSaving, setDeclSaving] = useState(false);
  const [deletingBillId, setDeletingBillId] = useState(null);
  const [reuploadExpId, setReuploadExpId] = useState(null);
  const reuploadInputRef = useRef(null);
  const [addPageExpId, setAddPageExpId] = useState(null);
  const addPageInputRef = useRef(null);

  // BF override modal state
  const [showBfModal, setShowBfModal] = useState(false);
  const [pendingBfOverrideReason, setPendingBfOverrideReason] = useState("");

  // Expense preview state (shows before final submit)
  const [showExpensePreview, setShowExpensePreview] = useState(false);

  // Shared expense state
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [companyEmployeesStatus, setCompanyEmployeesStatus] = useState("idle"); // idle | loading | loaded | error
  const [sharedBlocks, setSharedBlocks] = useState([]); // [{expense_date, category, shared_by_name}]
  const [sharedSearch, setSharedSearch] = useState("");

  // Cash bill state
  const [cashBillWarning, setCashBillWarning] = useState(false);   // day's vouchers over the cap
  const [cashApprovalSent, setCashApprovalSent] = useState(false); // approval requested
  // Cash vouchers already booked by this employee for the date in the form —
  // the ₹500 cap is a daily total across every trip, so this cannot be worked
  // out from the trip's own expense list.
  const [cashVoucherDay, setCashVoucherDay] = useState({ date: "", total: 0, limit: 500 });
  const [submissionReadiness, setSubmissionReadiness] = useState({
    can_submit: false,
    blockers: ["Checking submission requirements…"],
  });
  const [overspendResolution, setOverspendResolution] = useState("");
  const [savingOverspendResolution, setSavingOverspendResolution] = useState(false);
  const [advanceReturns, setAdvanceReturns] = useState([]);
  const [returnForm, setReturnForm] = useState({ amount:"", payment_method:"upi", payment_reference:"", payment_date:"", note:"", proof:null });
  const [returnSaving, setReturnSaving] = useState(false);

  // Ask Query (manager/admin only)
  const [tdQueryModal, setTdQueryModal] = useState(null); // { expenseId, label }
  const [tdQueryText,  setTdQueryText]  = useState("");
  const [tdQueryBusy,  setTdQueryBusy]  = useState(false);
  const canAskQuery = ["manager","admin","superadmin"].includes(user?.role);

  const handleTdAskQuery = async () => {
    if (!tdQueryText.trim() || !tdQueryModal) return;
    setTdQueryBusy(true);
    try {
      await API.post(`/queries/expense/${tdQueryModal.expenseId}`, { question: tdQueryText.trim() });
      toast.success("Query sent! Employee will be notified.");
      setTdQueryModal(null);
      setTdQueryText("");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to send query");
    } finally {
      setTdQueryBusy(false);
    }
  };

  const [form, setForm] = useState(DEFAULT_FORM);
  const [draftSaved, setDraftSaved] = useState(false);
  const expenseDraftKey = `ebek_expense_draft_${user?.id || "user"}_${id}`;

  useEffect(() => {
    if (!showModal || editingExpense) return;
    try {
      const saved = JSON.parse(localStorage.getItem(expenseDraftKey) || "null");
      if (saved?.form) {
        setForm(current => ({ ...current, ...saved.form, bill: null }));
        toast("Draft restored. Past you had your back.");
      }
    } catch (_) {}
  }, [showModal, editingExpense, expenseDraftKey]);

  useEffect(() => {
    if (!showModal || editingExpense) return;
    const hasContent = [form.description, form.amount, form.travel_from, form.travel_to, form.hotel_name,
      form.courier_name, form.fuel_vehicle_no, form.misc_category].some(value => String(value || "").trim());
    if (!hasContent) return;
    setDraftSaved(false);
    const timer = window.setTimeout(() => {
      const { bill, ...safeForm } = form;
      localStorage.setItem(expenseDraftKey, JSON.stringify({ safe_version: 1, form: safeForm }));
      setDraftSaved(true);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [showModal, editingExpense, form, expenseDraftKey]);

  const clearExpenseDraft = () => localStorage.removeItem(expenseDraftKey);

  const closeExpenseModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    setForm(DEFAULT_FORM);
    setBillPreview(null);
    setShowExpensePreview(false);
  };

  const openAddExpense = () => {
    setEditingExpense(null);
    setForm(DEFAULT_FORM);
    setBillPreview(null);
    setShowModal(true);
  };

  const parseJson = (value, fallback) => {
    if (!value) return fallback;
    if (typeof value === "object") return value;
    try { return JSON.parse(value); } catch { return fallback; }
  };

  const startEditExpense = (expense) => {
    const externalGroups = parseJson(expense.food_external_groups, []);
    const externalGroupMap = Array.isArray(externalGroups)
      ? Object.fromEntries(externalGroups.map(item => [item.type, String(item.count)])) : {};
    const stops = parseJson(expense.taxi_stops, []);
    const names = value => String(value || "").split(",").map(item => item.trim()).filter(Boolean);
    const sharedIds = String(expense.shared_with_ids || "").split(",").map(Number).filter(Number.isInteger);
    setEditingExpense(expense);
    setForm({
      ...DEFAULT_FORM,
      ...Object.fromEntries(Object.keys(DEFAULT_FORM).map(key => [key, expense[key] ?? DEFAULT_FORM[key]])),
      expense_date:toLocalDateInputValue(expense.expense_date),
      amount:String(expense.amount ?? ""),
      breakfast_included:Boolean(expense.breakfast_included),
      hotel_company_paid:Boolean(expense.hotel_company_paid),
      bill:null,
      odometer_start_photo:null,
      odometer_end_photo:null,
      oet_interlocutor_names:names(expense.oet_interlocutor_names),
      oet_freelancer_names:names(expense.oet_freelancer_names),
      oet_support_staff_names:names(expense.oet_support_staff_names),
      is_shared:sharedIds.length > 0,
      shared_with_ids:sharedIds,
      food_external_groups:externalGroupMap,
      multiple_stops:Array.isArray(stops) && stops.length > 0,
      extra_stops:Array.isArray(stops) ? stops : [],
    });
    setBillPreview(null);
    setShowModal(true);
  };

  useEffect(() => {
    if (!showModal) return;
    const frame = window.requestAnimationFrame(() => {
      expenseModalRef.current?.scrollTo({ top:0, behavior:"auto" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [showModal, editingExpense?.id]);

  const finishOfflineQueue = async (fd, options = {}) => {
    const count = await queueExpense(fd, { ...options, userId: user?.id });
    clearExpenseDraft();
    setShowModal(false);
    setShowBfModal(false);
    setShowExpensePreview(false);
    setForm(DEFAULT_FORM);
    setBillPreview(null);
    setCashBillWarning(false);
    setCashApprovalSent(false);
    toast.success(`Saved offline. ${count} expense${count === 1 ? " is" : "s are"} ready to sync.`);
  };

  const refreshSubmissionReadiness = useCallback(async () => {
    const response = await API.get(`/trips/${id}/submission-readiness`);
    setSubmissionReadiness(response.data);
    return response.data;
  }, [id]);

  const loadData = useCallback(() => {
    Promise.all([
      API.get(`/trips/${id}`),
      API.get(`/expenses/trip/${id}`),
      API.get(`/trips/${id}/submission-readiness`),
      API.get(`/advance-returns/mine`, { params:{ trip_id:id } })
    ]).then(([tripRes, expRes, readinessRes, returnsRes]) => {
      setTrip(tripRes.data);
      setExpenses(expRes.data);
      setSubmissionReadiness(readinessRes.data);
      setAdvanceReturns(returnsRes.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const downloadLatestTripExcel = async () => {
    try {
      const response = await API.get(`/expenses/trip/${id}`);
      const latestExpenses = response.data || [];
      setExpenses(latestExpenses);
      await downloadTripExpenseExcel(trip, latestExpenses);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not refresh the trip approval status for download.");
    }
  };

  const submitAdvanceReturn = async (event) => {
    event.preventDefault();
    if (!returnForm.proof) { toast.error("Upload the UPI, bank or cash receipt"); return; }
    setReturnSaving(true);
    try {
      const fd = new FormData();
      fd.append("trip_id", id);
      fd.append("amount", returnForm.amount);
      fd.append("payment_method", returnForm.payment_method);
      fd.append("payment_reference", returnForm.payment_reference.trim());
      fd.append("payment_date", returnForm.payment_date);
      fd.append("note", returnForm.note.trim());
      fd.append("proof", safeUploadFile(returnForm.proof));
      await API.post("/advance-returns", fd);
      toast.success("Return submitted to Finance for verification");
      setReturnForm({ amount:"", payment_method:"upi", payment_reference:"", payment_date:"", note:"", proof:null });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not submit the return. Please contact the Finance team.");
    } finally { setReturnSaving(false); }
  };

  useEffect(() => { loadData(); }, [loadData]);

  // Approval decisions can be made while the employee keeps this PWA screen
  // open. Keep the submission gate current instead of requiring a full reload.
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshSubmissionReadiness().catch(() => {});
    };
    const timer = window.setInterval(refreshWhenVisible, 15000);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshSubmissionReadiness]);

  // Load company employees + shared blocks whenever the Add Expense modal opens
  useEffect(() => {
    if (showModal) {
      setCompanyEmployeesStatus("loading");
      API.get("/expenses/company-employees")
        .then(r => { setCompanyEmployees(r.data); setCompanyEmployeesStatus("loaded"); })
        .catch(() => setCompanyEmployeesStatus("error"));
      API.get("/expenses/shared-blocks")
        .then(r => setSharedBlocks(r.data))
        .catch(() => {});
    }
  }, [showModal]);

  // Refresh the day's cash voucher total whenever the date changes while the
  // form is open, so the employee sees what is left of the ₹500 up front.
  useEffect(() => {
    if (!showModal || !form.expense_date) return;
    let cancelled = false;
    API.get(`/expenses/cash-voucher-day-total?date=${form.expense_date}`)
      .then(r => { if (!cancelled) setCashVoucherDay(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showModal, form.expense_date, expenses]);

  // Check if any hotel in this trip has breakfast included
  const hotelHasBf = expenses.some(e => e.category === "Hotel" && e.breakfast_included);

  // Check if any hotel expense is marked as company-paid (blocks Hotel category selection)
  const hotelCompanyPaidExists = expenses.some(e => e.category === "Hotel" && e.hotel_company_paid);

  // Reset form sub-fields when category changes
  const handleCategoryChange = (cat) => {
    const specialOetCategory = isOetSpecialCategory(cat);
    setForm(f => ({
      ...f,
      category: cat,
      bill_type: getBillTypeForCategory(f.bill_type, cat),
      food_subcategory: "",
      hotel_name: "",
      hotel_location: "",
      breakfast_included: false,
      bf_override_reason: "",
      cash_voucher_type: "",
      bill: specialOetCategory ? null : f.bill,
      travel_subcategory: "",
      travel_booked_by: "",
      travel_from: "",
      travel_to: "",
      travel_class: "",
      cab_type: "",
      cab_charge_type: "",
      cab_kms_travelled: "",
      cab_total_hours: "",
      cab_institutions_visited: "",
      fuel_vehicle_type: "",
      fuel_type: "",
      fuel_vehicle_no: "",
      fuel_liters: "",
      kms_travelled: "", odometer_start: "", odometer_end: "",
      odometer_start_photo: null, odometer_end_photo: null,
      courier_subtype: "",
      courier_name: "",
      courier_weight: "",
      courier_from: "", courier_to: "", courier_boxes: "", courier_tracking_ref: "",
      print_subcategory: "",
      misc_category: "",
      expense_subcategory: "",
      multiple_stops: false,
      extra_stops: [],
      oet_interlocutor_count: "",
      oet_interlocutor_names: [],
      oet_freelancer_count: "",
      oet_freelancer_names: [],
      oet_support_staff_count: "",
      oet_support_staff_names: [],
      catering_candidate_count: "",
      catering_ebek_staff_count: "",
      catering_support_staff_count: "",
      catering_bf_packs: "",
      catering_lunch_packs: "",
      catering_snacks_packs: "",
      is_shared: false,
      shared_with_ids: [],
      food_external_groups: {},
      food_pack_count: "",
    }));
    setBillPreview(null);
    setCashBillWarning(false);
    setCashApprovalSent(false);
  };

  const handleSubmitExpense = async (e) => {
    e.preventDefault();

    const isCompanyPaidCabEntry = form.category === "Travel"
      && form.travel_subcategory === "Cab"
      && form.travel_booked_by === "centralized";
    const companyPaidEntry = isCentralizedTrainBooking({ category: form.category, travelSubcategory: form.travel_subcategory, travelBookedBy: form.travel_booked_by })
      || isCompanyPaidHotelBlocked({ category: form.category, hotelCompanyPaid: form.hotel_company_paid })
      || isCompanyPaidCabEntry;

    const fuelValidationMessage = getFuelFieldValidationMessage({
      category: form.category,
      fuelVehicleType: form.fuel_vehicle_type,
      fuelType: form.fuel_type,
      fuelVehicleNo: form.fuel_vehicle_no,
      fuelLiters: form.fuel_liters,
    });
    if (fuelValidationMessage) {
      toast.error(fuelValidationMessage);
      return;
    }
    if (form.category === "Travel" && !form.travel_subcategory) {
      toast.error("Please select the mode of travel and complete all mandatory fields."); return;
    }
    const localTravel = form.category === "Travel" && ["Cab", "Auto", "Bike Taxi", "Others"].includes(form.travel_subcategory);
    if (localTravel && (!form.travel_from.trim() || !form.travel_to.trim() || !(Number(form.kms_travelled || form.cab_kms_travelled) > 0))) {
      toast.error("From, To and KMS travelled are required."); return;
    }
    if (form.category === "Travel" && ["Train", "Bus", "Flight"].includes(form.travel_subcategory)
      && (!form.travel_booked_by || !form.travel_from.trim() || !form.travel_to.trim() || !form.travel_class)) {
      toast.error("Booked by, From, To and Class are mandatory for this travel mode."); return;
    }
    if (form.category === "Travel" && form.travel_subcategory === "Cab"
      && (!form.travel_booked_by || !form.cab_type || !form.cab_charge_type
        || (form.cab_charge_type === "full_day" && (!(Number(form.cab_total_hours) > 0) || !(Number(form.cab_institutions_visited) > 0))))) {
      toast.error("Complete the cab type, charge type and all mandatory travel details."); return;
    }
    if (form.multiple_stops && form.extra_stops.some(stop => !stop.from?.trim() || !stop.to?.trim())) {
      toast.error("Complete the From and To fields for every stop."); return;
    }
    if (PERSON_DETAIL_CATEGORIES.has(form.category)) {
      const isInterlocutor = form.category.startsWith("Interlocutor");
      const isSupport = form.category === "Support Staff";
      const count = isInterlocutor ? form.oet_interlocutor_count : isSupport ? form.oet_support_staff_count : form.oet_freelancer_count;
      const names = isInterlocutor ? form.oet_interlocutor_names : isSupport ? form.oet_support_staff_names : form.oet_freelancer_names;
      if (!(Number(count) > 0) || names.length !== Number(count) || names.some(name => !String(name || "").trim())) {
        toast.error("Total number of persons and every person's name are mandatory for this voucher."); return;
      }
    }
    if (form.category === "Catering Charges") {
      const cateringValues = [
        form.catering_candidate_count, form.catering_ebek_staff_count, form.catering_support_staff_count,
        form.catering_bf_packs, form.catering_lunch_packs, form.catering_snacks_packs,
      ];
      if (cateringValues.some(value => value === "" || Number(value) < 0)
        || (Number(form.catering_bf_packs) + Number(form.catering_lunch_packs) + Number(form.catering_snacks_packs) <= 0)) {
        toast.error("Enter all catering headcounts and pack quantities. At least one meal pack is required."); return;
      }
    }
    if (form.category === "Hotel" && (!form.hotel_name.trim() || !form.hotel_location.trim())) {
      toast.error("Hotel name and location are required."); return;
    }
    if (form.category === "Courier" && (!form.courier_subtype || !form.courier_name.trim() || !form.courier_from.trim()
      || !form.courier_to.trim() || !(Number(form.courier_boxes) > 0) || !(Number(form.courier_weight) > 0)
      || !form.courier_tracking_ref.trim())) {
      toast.error("Complete all mandatory courier details."); return;
    }
    if (form.category === "Fuel" && (!(Number(form.odometer_end) > Number(form.odometer_start))
      || (!form.odometer_start_photo && !editingExpense?.odometer_start_photo)
      || (!form.odometer_end_photo && !editingExpense?.odometer_end_photo))) {
      toast.error("Enter valid odometer readings and upload both odometer photos."); return;
    }

    // Ebekmed category sub-type required
    if (SUBCATEGORY_REQUIRED_CATEGORIES.includes(form.category) && !form.expense_subcategory) {
      const label = form.category === "Medical Equipment"   ? "Equipment Reason"
                  : form.category === "Medical Consumables" ? "Consumable Reason"
                  : "Delivery Type";
      toast.error(`Please select the ${label}.`);
      return;
    }

    // BF override check
    if (form.category === "Food" && form.food_subcategory === "BF" && hotelHasBf && !form.bf_override_reason) {
      setShowBfModal(true);
      return;
    }
    const externalPeopleCount = Object.values(form.food_external_groups || {})
      .reduce((sum, count) => sum + (Number(count) || 0), 0);
    const peopleServed = 1 + form.shared_with_ids.length + externalPeopleCount;
    if (form.category === "Food" && (form.shared_with_ids.length || externalPeopleCount)
      && (!Number.isInteger(Number(form.food_pack_count)) || Number(form.food_pack_count) < peopleServed)) {
      toast.error(`Enter at least ${peopleServed} food packs for the people served.`); return;
    }

    // Cash Voucher sub-type dropdown has been removed — no validation needed.

    if (!companyPaidEntry && !form.bill && !editingExpense?.bill_url
      && (form.bill_type === "bill" || form.bill_type === "cash_voucher")) {
      toast.error("Please upload the bill / voucher photo");
      return;
    }
    setSaving(true);
    let offlineRequest = null;
    const clientSyncId = `expense-${user?.id || "user"}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const fd = new FormData();
      fd.append("trip_id", id);
      fd.append("expense_date", form.expense_date);
      fd.append("category", form.category);
      fd.append("description", form.description);
      fd.append("amount", form.amount);
      fd.append("bill_type", form.bill_type);
      if (form.food_subcategory) fd.append("food_subcategory", form.food_subcategory);
      if (form.hotel_name) fd.append("hotel_name", form.hotel_name);
      if (form.hotel_location) fd.append("hotel_location", form.hotel_location);
      fd.append("breakfast_included", form.breakfast_included ? "true" : "false");
      fd.append("hotel_company_paid", form.hotel_company_paid ? "true" : "false");
      if (form.bf_override_reason) fd.append("bf_override_reason", form.bf_override_reason);
      if (form.cash_voucher_type) fd.append("cash_voucher_type", form.cash_voucher_type);
      if (form.travel_subcategory) fd.append("travel_subcategory", form.travel_subcategory);
      if (form.travel_booked_by)  fd.append("travel_booked_by",  form.travel_booked_by);
      // Require From/To for Travel → Cab/Auto subtypes
    const needsFromTo = form.category === "Travel" && ["Auto", "Cab"].includes(form.travel_subcategory);
    if (needsFromTo && !form.travel_from?.trim()) {
      toast.error("Please enter the 'From' location for this expense.");
      return;
    }
    if (needsFromTo && !form.travel_to?.trim()) {
      toast.error("Please enter the 'To' location for this expense.");
      return;
    }
    if (form.travel_from)       fd.append("travel_from",       form.travel_from);
      if (form.travel_to)         fd.append("travel_to",         form.travel_to);
      if (form.travel_class)      fd.append("travel_class",      form.travel_class);
      if (form.cab_type)               fd.append("cab_type",               form.cab_type);
      if (form.cab_charge_type)        fd.append("cab_charge_type",        form.cab_charge_type);
      if (form.cab_kms_travelled)      fd.append("cab_kms_travelled",      form.cab_kms_travelled);
      if (form.cab_total_hours)        fd.append("cab_total_hours",        form.cab_total_hours);
      if (form.cab_institutions_visited) fd.append("cab_institutions_visited", form.cab_institutions_visited);
      if (form.fuel_vehicle_type) fd.append("fuel_vehicle_type", form.fuel_vehicle_type);
      if (form.fuel_type) fd.append("fuel_type", form.fuel_type);
      if (form.fuel_vehicle_no) fd.append("fuel_vehicle_no", form.fuel_vehicle_no);
      if (form.fuel_liters) fd.append("fuel_liters", form.fuel_liters);
      if (form.kms_travelled) fd.append("kms_travelled", form.kms_travelled);
      if (form.odometer_start) fd.append("odometer_start", form.odometer_start);
      if (form.odometer_end) fd.append("odometer_end", form.odometer_end);
      if (form.odometer_start_photo) fd.append("odometer_start_photo", safeUploadFile(form.odometer_start_photo));
      if (form.odometer_end_photo) fd.append("odometer_end_photo", safeUploadFile(form.odometer_end_photo));
      if (form.courier_subtype)    fd.append("courier_subtype", form.courier_subtype);
      if (form.courier_name)       fd.append("courier_name", form.courier_name);
      if (form.courier_weight)     fd.append("courier_weight", form.courier_weight);
      if (form.courier_from) fd.append("courier_from", form.courier_from);
      if (form.courier_to) fd.append("courier_to", form.courier_to);
      if (form.courier_boxes) fd.append("courier_boxes", form.courier_boxes);
      if (form.courier_tracking_ref) fd.append("courier_tracking_ref", form.courier_tracking_ref);
      if (form.print_subcategory)    fd.append("print_subcategory", form.print_subcategory);
      if (form.misc_category)        fd.append("misc_category", form.misc_category);
      if (form.expense_subcategory)  fd.append("expense_subcategory", form.expense_subcategory);
      if (form.oet_interlocutor_count) fd.append("oet_interlocutor_count", form.oet_interlocutor_count);
      if (form.oet_interlocutor_names?.length > 0) fd.append("oet_interlocutor_names", form.oet_interlocutor_names.filter(Boolean).join(", "));
      if (form.oet_freelancer_count) fd.append("oet_freelancer_count", form.oet_freelancer_count);
      if (form.oet_freelancer_names?.length > 0) fd.append("oet_freelancer_names", form.oet_freelancer_names.filter(Boolean).join(", "));
      if (form.oet_support_staff_count) fd.append("oet_support_staff_count", form.oet_support_staff_count);
      if (form.oet_support_staff_names?.length > 0) fd.append("oet_support_staff_names", form.oet_support_staff_names.filter(Boolean).join(", "));
      ["catering_candidate_count", "catering_ebek_staff_count", "catering_support_staff_count",
        "catering_bf_packs", "catering_lunch_packs", "catering_snacks_packs"].forEach(field => {
        fd.append(field, form[field]);
      });
      appendFoodSharingFields(fd, form);
      if (form.multiple_stops && form.extra_stops?.length > 0)
        fd.append("taxi_stops", JSON.stringify(form.extra_stops));
      if (form.bill) fd.append("bill", safeUploadFile(form.bill));

      if (!editingExpense) fd.append("client_sync_id", clientSyncId);
      offlineRequest = fd;

      if (!navigator.onLine) {
        if (editingExpense) {
          toast.error("Editing an expense requires an internet connection. Your changes remain open.");
          return;
        }
        await finishOfflineQueue(fd, { needsApproval });
        return;
      }

      let expRes;
      if (editingExpense) {
        expRes = await API.patch(`/expenses/${editingExpense.id}`, fd);
      } else {
        try {
          expRes = await API.post("/expenses", fd);
        } catch (postError) {
          if (postError.response?.status !== 409 || postError.response?.data?.error !== "client_sync_conflict") throw postError;
          fd.set("client_sync_id", `expense-${user?.id || "user"}-${Date.now()}-${Math.random().toString(36).slice(2)}`);
          expRes = await API.post("/expenses", fd);
        }
      }

      const confirmedExpenseId = expRes.data?.id
        || expRes.data?.expense_id
        || expRes.headers?.["x-expense-id"];
      if (!confirmedExpenseId) {
        toast.error(`The server did not confirm the ${editingExpense ? "expense update" : "new expense record"}. Your form has been kept open.`, { duration: 8000 });
        return;
      }

      const confirmedExpense = { ...expRes.data, id: Number(confirmedExpenseId) };

      // Put the confirmed row on screen immediately. The background reload then
      // enriches it with approval and attachment metadata from the list API.
      setExpenses(current => editingExpense
        ? current.map(item => Number(item.id) === Number(confirmedExpense.id) ? { ...item, ...confirmedExpense } : item)
        : current.some(item => Number(item.id) === Number(confirmedExpense.id)) ? current : [confirmedExpense, ...current]);

      // Approval creation is performed atomically by the API. Trust the server
      // response rather than issuing a second request that can fail after the
      // expense itself has already been saved.
      if (editingExpense && expRes.data.approval_reset) {
        toast.success(expRes.data.amount_changed
          ? `Expense #${confirmedExpense.id} amount updated. Manager and Finance must approve it again.`
          : `Expense #${confirmedExpense.id} updated and sent back for approval.`, { duration: 7000 });
      } else if (editingExpense) {
        toast.success(`Expense #${confirmedExpense.id} updated.`, { duration: 6000 });
      } else if (expRes.data.approval_required) {
        toast.success(`Expense #${confirmedExpense.id} saved and sent for approval.`, { duration: 6000 });
      } else if (expRes.data?.duplicate) {
        toast.success(`Expense #${confirmedExpense.id} was already saved. The trip list has been refreshed.`, { duration: 6000 });
      } else {
        toast.success(`Expense #${confirmedExpense.id} saved and added to this trip.`, { duration: 6000 });
      }
      if (form.bill) celebrate("billSaved");

      // Show any policy warnings (expense was saved, but manager will see it flagged)
      if (expRes.data.policyWarnings?.length > 0) {
        expRes.data.policyWarnings.forEach(w =>
          toast(`⚠️ ${w.message}`, { duration: 7000, style: { background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" } })
        );
      }
      setShowModal(false);
      setEditingExpense(null);
      clearExpenseDraft();
      setForm(DEFAULT_FORM);
      setBillPreview(null);
      setCashBillWarning(false);
      setCashApprovalSent(false);
      setSharedBlocks([]); // will reload next time modal opens
      loadData();
    } catch (err) {
      if (!err.response && navigator.onLine === false) {
        try {
          if (!offlineRequest) throw new Error("Expense was not ready to queue.");
          await finishOfflineQueue(offlineRequest, { needsApproval });
          return;
        } catch (queueError) {
          toast.error(queueError.message || "Could not save offline.");
        }
      }
      const isBlocked = err.response?.data?.error === "policy_blocked";
      const serverUnreachable = !err.response;
      toast.error(
        isBlocked
          ? `🚫 ${err.response.data.blockMessage}`
          : serverUnreachable
            ? "The server could not be reached. This expense was not submitted; your form is still open. Check the connection and retry once."
            : (err.response?.data?.error || "Failed to add expense"),
        { duration: isBlocked || serverUnreachable ? 8000 : 4000 }
      );
    } finally { setSaving(false); }
  };

  // BF override confirmation — called from the BF override modal
  const handleConfirmBfOverride = () => {
    if (!pendingBfOverrideReason) {
      toast.error("Please select a reason for the override.");
      return;
    }
    setForm(f => ({ ...f, bf_override_reason: pendingBfOverrideReason }));
    setShowBfModal(false);
    // Submit through the normal form path after React has applied the reason.
    // This is important while editing: the normal path PATCHes the existing
    // expense, whereas the old duplicate code always created a new row.
    window.setTimeout(() => {
      document.getElementById("add-expense-form")?.requestSubmit();
    }, 0);
  };

  // Request approval when the day's cash vouchers together go over ₹500
  // Expense is saved without bill/voucher first; employee uploads after both approve
  const handleRequestCashApproval = async () => {
    if (!form.description || !form.amount) {
      toast.error("Please fill in description and amount before requesting approval.");
      return;
    }
    setSaving(true);
    try {
      // Save the expense WITHOUT a bill (bill will be uploaded after approval)
      const fd = new FormData();
      fd.append("trip_id", id);
      fd.append("expense_date", form.expense_date);
      fd.append("category", form.category);
      fd.append("description", form.description);
      fd.append("amount", form.amount);
      fd.append("bill_type", form.bill_type);
      if (form.food_subcategory) fd.append("food_subcategory", form.food_subcategory);
      appendFoodSharingFields(fd, form);
      // No bill attached — employee uploads after approval

      const cashRes = await API.post("/expenses", fd);
      const newExpId = cashRes.data.id;

      // Send approval request to manager + finance
      await API.post(`/expenses/${newExpId}/request-cash-bill-approval`);

      toast.success("Approval requested! Manager & Finance have been notified. Upload the bill once approved.");
      if (cashRes.data.policyWarnings?.length > 0) {
        cashRes.data.policyWarnings.forEach(w =>
          toast(`⚠️ ${w.message}`, { duration: 7000, style: { background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" } })
        );
      }
      setShowModal(false);
      clearExpenseDraft();
      setForm(DEFAULT_FORM);
      setBillPreview(null);
      setCashBillWarning(false);
      setCashApprovalSent(false);
      loadData();
    } catch (err) {
      const isBlocked = err.response?.data?.error === "policy_blocked";
      toast.error(
        isBlocked ? `🚫 ${err.response.data.blockMessage}` : (err.response?.data?.error || "Failed to request approval"),
        { duration: isBlocked ? 8000 : 4000 }
      );
    } finally { setSaving(false); }
  };

  const handleDelete = async (expId) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await API.delete(`/expenses/${expId}`);
      toast.success("Expense deleted");
      loadData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeleteBill = async (expId) => {
    if (!window.confirm("Delete the bill photo from this expense? The expense entry will remain.")) return;
    setDeletingBillId(expId);
    try {
      await API.delete(`/expenses/${expId}/bill`);
      toast.success("Bill deleted");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete bill");
    } finally { setDeletingBillId(null); }
  };

  const handleReuploadBillFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !reuploadExpId) return;
    const expId = reuploadExpId;
    setReuploadExpId(null);
    try {
      let finalFile;
      if (file.type === "application/pdf") {
        toast.loading("Reading PDF…", { id: "pdf-convert-reup" });
        finalFile = await convertPdfToJpeg(file, {
          onProgress: (done, total) => {
            toast.loading(
              total > 1 ? `Converting page ${done}/${total}…` : "Converting PDF…",
              { id: "pdf-convert-reup" }
            );
          },
        });
        toast.dismiss("pdf-convert-reup");
      } else {
        finalFile = await compressImage(file);
      }
      const fd = new FormData();
      fd.append("bill", safeUploadFile(finalFile));
      toast.loading("Uploading bill...", { id: "reup-upload" });
      await API.patch(`/expenses/${expId}/bill`, fd);
      toast.dismiss("reup-upload");
      toast.success("Bill replaced successfully!");
      loadData();
    } catch (err) {
      toast.dismiss("pdf-convert-reup");
      toast.dismiss("reup-upload");
      toast.error(err.response?.data?.error || "Failed to replace bill");
    }
  };

  const handleAddBillPage = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !addPageExpId) return;
    const expId = addPageExpId;
    setAddPageExpId(null);
    const toastId = "add-page";
    try {
      // Find the expense to get its bill URL
      const exp = expenses.find(ex => ex.id === expId);
      if (!exp || !exp.bill_url) { toast.error("No existing bill to append to"); return; }

      toast.loading("Fetching existing bill...", { id: toastId });

      // Load existing bill via backend proxy (CORS-safe)
      let proxyParams;
      if (exp.bill_public_id && !exp.bill_public_id.includes("/")) {
        // Google Drive file ID
        proxyParams = { file_id: exp.bill_public_id };
      } else if (exp.bill_public_id) {
        // Legacy Cloudinary — get signed URL first
        const r = await API.get("/expenses/signed-bill-url", { params: { public_id: exp.bill_public_id } });
        proxyParams = { url: r.data.url };
      } else {
        proxyParams = { url: exp.bill_url };
      }
      const proxyRes = await API.get(`/expenses/${exp.id}/bill-file`, { responseType: "blob" });
      const existingBlob = proxyRes.data;

      // Process new page (convert PDF or compress image)
      toast.loading("Processing new page...", { id: toastId });
      let newFile;
      if (file.type === "application/pdf") {
        newFile = await convertPdfToJpeg(file, {
          onProgress: (done, total) => {
            toast.loading(
              total > 1 ? `Converting page ${done}/${total}…` : "Converting PDF…",
              { id: toastId }
            );
          },
        });
      } else {
        newFile = await compressImage(file);
      }

      // Merge existing bill + new page on a single canvas
      toast.loading("Merging pages...", { id: toastId });
      const loadImg = (src) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        if (typeof src === "string") { img.src = src; }
        else { img.src = URL.createObjectURL(src); }
      });

      const [imgA, imgB] = await Promise.all([
        loadImg(existingBlob),
        loadImg(newFile),
      ]);

      const GAP = 20;
      const mergedWidth = Math.max(imgA.naturalWidth || imgA.width, imgB.naturalWidth || imgB.width);
      const mergedHeight = (imgA.naturalHeight || imgA.height) + GAP + (imgB.naturalHeight || imgB.height);
      const canvas = document.createElement("canvas");
      canvas.width = mergedWidth;
      canvas.height = mergedHeight;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, mergedWidth, mergedHeight);
      ctx.drawImage(imgA, 0, 0);
      ctx.drawImage(imgB, 0, (imgA.naturalHeight || imgA.height) + GAP);

      const mergedFile = await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(new File([blob], `bill_${expId}_merged.jpg`, { type: "image/jpeg" }));
        }, "image/jpeg", 0.85);
      });

      // Upload merged file
      toast.loading("Uploading merged bill...", { id: toastId });
      const fd = new FormData();
      fd.append("bill", safeUploadFile(mergedFile));
      await API.patch(`/expenses/${expId}/bill`, fd);
      toast.dismiss(toastId);
      toast.success("Page added — bill updated with both pages!");
      loadData();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.error || "Failed to add page");
    }
  };

  const handleViewBill = async (exp) => {
    {
      try {
        const response = await API.get(`/expenses/${exp.id}/bill-file`, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch { toast.error("Failed to load bill"); }
      return;
    }
    if (exp.bill_public_id && !exp.bill_public_id.includes("/")) {
      window.open(exp.bill_url, "_blank");
      return;
    }
    const win = window.open("", "_blank");
    if (!win) { toast.error("Allow popups to view bills"); return; }
    win.document.write("<html><body style='margin:0;padding:20px;font-family:sans-serif'><p>Loading bill...</p></body></html>");
    try {
      if (exp.bill_public_id) {
        const r = await API.get("/expenses/signed-bill-url", { params: { public_id: exp.bill_public_id } });
        win.location.href = r.data.url;
      } else {
        win.location.href = exp.bill_url;
      }
    } catch {
      win.close();
      toast.error("Failed to load bill");
    }
  };

  const handleSubmitTrip = async () => {
    try {
      const latestReadiness = await refreshSubmissionReadiness();
      if (!latestReadiness.can_submit) {
        toast.error(latestReadiness.blockers?.[0] || "Complete the outstanding review before submitting.");
        return;
      }
      const employeeSpent = expenses.reduce((sum, expense) =>
        sum + (Number(expense.employee_reimbursable) === 1 ? Number(expense.amount || 0) : 0), 0);
      const currentOverspend = Math.max(0, employeeSpent - parseFloat(trip?.advance_amount || 0));
      if (currentOverspend > 0.005 && !overspendResolution) {
        toast.error("Choose how Finance should settle the excess expense.");
        return;
      }
      if (!window.confirm("Submit this trip for review? You won't be able to add more expenses after submission.")) return;
      await API.patch(`/trips/${id}/submit`, { overspend_resolution: currentOverspend > 0.005 ? overspendResolution : undefined });
      toast.success("Trip submitted—you’re all set ✨");
      celebrate("expenseSent", { message: "All expenses and bills are with Finance" });
      loadData();
    } catch (err) {
      const blockers = err.response?.data?.blockers;
      toast.error(blockers?.[0] || err.response?.data?.error || "Failed to submit trip");
      if (blockers) setSubmissionReadiness({ can_submit:false, blockers });
    }
  };

  const saveLegacyOverspendResolution = async () => {
    if (!overspendResolution) {
      toast.error("Choose how Finance should settle the excess expense.");
      return;
    }
    setSavingOverspendResolution(true);
    try {
      const response = await API.patch(`/trips/${id}/overspend-resolution`, {
        overspend_resolution: overspendResolution,
      });
      toast.success(response.data?.message || "Settlement option saved.");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save the settlement option.");
    } finally { setSavingOverspendResolution(false); }
  };

  const handleResubmitTrip = async () => {
    if (!window.confirm("Resubmit this trip for review? Make sure all corrected bills have been uploaded.")) return;
    try {
      await API.patch(`/trips/${id}/resubmit`);
      toast.success("Trip resubmitted! Finance team has been notified.");
      loadData();
    } catch (err) { toast.error(err.response?.data?.error || "Failed to resubmit trip"); }
  };

  // convertPdfToJpeg is imported from ../../utils/pdfConverter (bundled, no CDN)

  const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX_WIDTH = 1600;
        let { width, height } = img;
        if (width > MAX_WIDTH) { height = Math.round((height * MAX_WIDTH) / width); width = MAX_WIDTH; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          // Camera-captured photos on some mobile browsers have no filename extension
          // (or no filename at all) — always force a valid .jpg name rather than relying
          // on the original name having a dot to replace.
          const baseName = (file.name || "bill").replace(/\.[^.]+$/, "") || "bill";
          resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
        }, "image/jpeg", 0.75);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCashBillWarning(false);
    try {
      let finalFile;
      if (file.type === "application/pdf") {
        toast.loading("Reading PDF…", { id: "pdf-convert" });
        finalFile = await convertPdfToJpeg(file, {
          onProgress: (done, total) => {
            toast.loading(
              total > 1 ? `Converting page ${done}/${total}…` : "Converting PDF…",
              { id: "pdf-convert" }
            );
          },
        });
        toast.dismiss("pdf-convert");
      } else {
        finalFile = await compressImage(file);
      }
      setForm(f => ({ ...f, bill: finalFile }));
      setBillPreview(URL.createObjectURL(finalFile));

      // Cash Bill removed — no warning needed
    } catch (err) {
      toast.dismiss("pdf-convert");
      console.error("Bill file processing failed:", err);
      toast.error(`Could not process file: ${err?.message || "unknown error"}. Try uploading a photo instead.`);
    }
  };

  if (loading) return <div style={{ textAlign:"center", padding: 60, color:"#9ca3af" }}>Loading trip details...</div>;
  if (!trip) return <div style={{ textAlign:"center", padding: 60 }}>Trip not found</div>;

  const employeeSpent = expenses.reduce((sum, expense) =>
    sum + (Number(expense.employee_reimbursable) === 1 ? Number(expense.amount || 0) : 0), 0);
  const balance = parseFloat(trip.advance_amount||0) - employeeSpent;
  const overspendAmount = Math.max(0, -balance);
  const verifiedReturned = advanceReturns.filter(r => r.status === "verified").reduce((sum, r) => sum + Number(r.verified_amount || 0), 0);
  const pendingReturned = advanceReturns.filter(r => ["pending","queried"].includes(r.status)).reduce((sum, r) => sum + Number(r.submitted_amount || 0), 0);
  const returnOutstanding = Math.max(0, balance - verifiedReturned - pendingReturned);

  // ── Deferred OET Declaration: show form before any expense entry ─────────
  const needsDeclaration = trip.oet_declaration_deferred && trip.title === "OET Examination";

  const handleSaveDeclaration = async () => {
    if (!declForm.oet_candidate_count) { toast.error("Please enter No. of Candidates before proceeding."); return; }
    setDeclSaving(true);
    try {
      await API.patch(`/trips/${id}/oet-declaration`, declForm);
      setTrip(t => ({ ...t, oet_declaration_deferred: false,
        oet_candidate_count: declForm.oet_candidate_count,
        oet_interlocutor_count: declForm.oet_interlocutor_count,
        oet_freelancer_count: declForm.oet_freelancer_count,
        oet_support_staff_count: declForm.oet_support_staff_count,
      }));
      toast.success("Declaration saved! You can now add expenses.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save declaration");
    } finally { setDeclSaving(false); }
  };

  if (needsDeclaration) {
    return (
      <div style={{ maxWidth:520, margin:"40px auto", padding:"0 16px" }}>
        <div style={{ background:"white", borderRadius:20, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", overflow:"hidden" }}>
          <div style={{ background:"#E31E24", padding:"24px 28px", color:"white" }}>
            <div style={{ fontSize:20, fontWeight:800 }}>📋 Staff & Candidate Declaration</div>
            <div style={{ fontSize:13, opacity:0.85, marginTop:4 }}>Required before you can add expenses for this OET exam</div>
          </div>
          <div style={{ padding:"24px 28px" }}>
            <div style={{ padding:"10px 14px", background:"#FFF7ED", border:"1px solid #FDE68A", borderRadius:10, fontSize:13, color:"#92400e", marginBottom:20 }}>
              ℹ️ You chose to declare staff &amp; candidate numbers during expense submission. Please fill in the details below before adding your expenses. Finance will cross-check these against the center's bills.
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">No. of Candidates <span style={{ color:"#DC2626" }}>*</span></label>
                <input className="form-control" type="number" min="0" placeholder="e.g. 30"
                  value={declForm.oet_candidate_count}
                  onChange={e => setDeclForm(f => ({ ...f, oet_candidate_count:e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">No. of Support Staff</label>
                <input className="form-control" type="number" min="0" placeholder="e.g. 8"
                  value={declForm.oet_support_staff_count}
                  onChange={e => setDeclForm(f => ({ ...f, oet_support_staff_count:e.target.value }))} />
              </div>
            </div>
            {parseInt(declForm.oet_support_staff_count||0)>0 && (
              <div className="form-group">
                <label className="form-label">Support Staff Names <span style={{ fontSize:11, color:"#9ca3af" }}>(comma-separated)</span></label>
                <textarea className="form-control" rows={2} placeholder="e.g. Ravi Kumar, Meena S..."
                  value={declForm.oet_support_staff_names}
                  onChange={e => setDeclForm(f => ({ ...f, oet_support_staff_names:e.target.value }))}
                  style={{ resize:"none" }} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">No. of Interlocutors</label>
              <input className="form-control" type="number" min="0" placeholder="e.g. 4"
                value={declForm.oet_interlocutor_count}
                onChange={e => setDeclForm(f => ({ ...f, oet_interlocutor_count:e.target.value,
                  oet_interlocutor_names: Array(parseInt(e.target.value)||0).fill("").join(",") }))} />
            </div>
            {parseInt(declForm.oet_interlocutor_count||0)>0 && (
              <div className="form-group">
                <label className="form-label">Interlocutor Names <span style={{ fontSize:11, color:"#9ca3af" }}>(comma-separated)</span></label>
                <textarea className="form-control" rows={2} placeholder="e.g. Dr. Anitha K, Mr. Rajan S..."
                  value={declForm.oet_interlocutor_names}
                  onChange={e => setDeclForm(f => ({ ...f, oet_interlocutor_names:e.target.value }))}
                  style={{ resize:"none" }} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">No. of Freelancers</label>
              <input className="form-control" type="number" min="0" placeholder="e.g. 2"
                value={declForm.oet_freelancer_count}
                onChange={e => setDeclForm(f => ({ ...f, oet_freelancer_count:e.target.value }))} />
            </div>
            {parseInt(declForm.oet_freelancer_count||0)>0 && (
              <div className="form-group">
                <label className="form-label">Freelancer Names <span style={{ fontSize:11, color:"#9ca3af" }}>(comma-separated)</span></label>
                <textarea className="form-control" rows={2} placeholder="e.g. Suresh M, Priya N..."
                  value={declForm.oet_freelancer_names}
                  onChange={e => setDeclForm(f => ({ ...f, oet_freelancer_names:e.target.value }))}
                  style={{ resize:"none" }} />
              </div>
            )}

            <button
              onClick={handleSaveDeclaration}
              disabled={declSaving || !declForm.oet_candidate_count}
              style={{ width:"100%", padding:"13px", borderRadius:12, border:"none", cursor:"pointer",
                       fontWeight:700, fontSize:15, marginTop:8,
                       background: !declForm.oet_candidate_count ? "#d1d5db" : "#E31E24",
                       color: !declForm.oet_candidate_count ? "#9ca3af" : "white" }}>
              {declSaving ? "Saving..." : "✅ Save Declaration & Add Expenses"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // OET Examination check: shows the dedicated OET expense module.
  const isOetTrip = isOetTripForRules({ company: user?.company, tripTitle: trip?.title });
  const isOetModuleCategory = isOetSpecialCategory(form.category);
  const isEbekmed = user?.company === "ebekmed";

  // Cash voucher sub-type rules:
  //   undefined  → no sub-type dropdown (ebekmed or non-ebek)
  //   []         → no sub-type dropdown (other ebek trip categories)
  //   [...]      → sub-type dropdown shown
  // The Cash Voucher TYPE sub-dropdown is shown ONLY for OET special categories.
  // All other categories can still select Cash Voucher, but no sub-type is required.
  const cashVoucherTypes = (() => {
    if (!trip || user?.company !== "ebek") return undefined;
    if (isOetTrip) return undefined;
    if (trip.title === "Cambridge Examination") return CAMBRIDGE_CASH_VOUCHER_TYPES;
    return undefined; // All other Ebek trips: Cash Voucher available, no sub-type needed
  })();
  // Cash Voucher bill type is always available for all categories
  const cashVoucherAllowed = true;

  // Cash Bill option removed — no longer available for any company.
  // Cash voucher: Venue Manager / Asst VM on an OET trip have no amount restriction.
  // Everyone else has a ₹500 soft limit for the DAY, not for one voucher — every
  // cash voucher dated the same day counts towards it, across every trip. Going
  // over is still allowed; it just triggers manager approval.
  const oetCategoryLimit = getOetCashVoucherApprovalLimit(form.category);
  const cashVoucherLimit = oetCategoryLimit || cashVoucherDay.limit || 500;
  const userEmail = (user?.email || "").toLowerCase();
  const isVenueManager = !!(trip && (
    (trip.oet_venue_manager_email || "").toLowerCase() === userEmail ||
    (trip.oet_assistant_venue_manager_email || "").toLowerCase() === userEmail
  ));
  // Vouchers already booked for the date in the form, plus what is being added now.
  const cashVoucherBooked = cashVoucherDay.date === form.expense_date
    ? Number(cashVoucherDay.total || 0) : 0;
  const cashVoucherDayTotal = form.bill_type === "cash_voucher"
    ? (oetCategoryLimit ? (parseFloat(form.amount || 0) || 0) : cashVoucherBooked + (parseFloat(form.amount || 0) || 0))
    : cashVoucherBooked;
  const cashVoucherRemaining = Math.max(0, cashVoucherLimit - cashVoucherBooked);
  const isCashVoucherOverLimit = form.bill_type === "cash_voucher"
    && parseFloat(form.amount || 0) > 0
    && cashVoucherDayTotal > cashVoucherLimit
    && (oetCategoryLimit ? true : !isVenueManager);
  // Needs approval if the day's cash vouchers go over the limit (for non-VM employees)
  const needsApproval = (isEbekmed && form.bill_type === "cash_voucher")
    || isCashVoucherOverLimit
    || form.category === "Catering Charges";

  // Shared block: someone else has already included this employee in a shared
  // Travel/Hotel/Food expense for the same date — block them from adding the same category
  // For Travel / Hotel — block the whole category if someone included this employee
  const sharedBlockEntry = ["Travel","Hotel"].includes(form.category)
    ? sharedBlocks.find(
        b => String(b.expense_date).substring(0,10) === form.expense_date
          && b.category === form.category
      )
    : null;

  // For Food — only the specific sub-types that were shared are blocked (not the whole category)
  const blockedFoodSubtypes = form.category === "Food"
    ? sharedBlocks
        .filter(b => String(b.expense_date).substring(0,10) === form.expense_date
          && b.category === "Food"
          && b.food_subcategory)
        .map(b => b.food_subcategory)
    : [];

  // isSharedBlocked = true only when the currently selected sub-type is blocked (or whole category blocked)
  const isSharedBlocked = !!sharedBlockEntry
    || (form.category === "Food" && !!form.food_subcategory && blockedFoodSubtypes.includes(form.food_subcategory));
  const isCentralizedTrainBlocked = isCentralizedTrainBooking({
    category: form.category,
    travelSubcategory: form.travel_subcategory,
    travelBookedBy: form.travel_booked_by,
  });
  const isCompanyPaidHotelEntry = isCompanyPaidHotelBlocked({
    category: form.category,
    hotelCompanyPaid: form.hotel_company_paid,
  });
  const isCompanyPaidCabEntry = form.category === "Travel"
    && form.travel_subcategory === "Cab"
    && form.travel_booked_by === "centralized";
  const isCompanyPaidEntry = isCentralizedTrainBlocked || isCompanyPaidHotelEntry || isCompanyPaidCabEntry;
  const isExpenseEntryBlocked = isSharedBlocked;
  const showClaimFields = true;

  // Build category breakdown including OET special + Ebekmed extra + Petty Cash categories
  const isPettyCashTrip = trip?.title === "Petty Cash";
  const allKnownCats = [
    ...getExpenseCategoriesForTrip(CATEGORIES, { company: user?.company, tripTitle: trip?.title }),
    ...(isEbekmed ? EBEKMED_EXTRA_CATEGORIES : []),
    ...(isPettyCashTrip ? PETTY_CASH_CATEGORIES : []),
  ];
  const byCat = allKnownCats.map(c => ({
    cat: c,
    total: expenses.filter(e => e.category === c).reduce((s,e) => s + parseFloat(e.amount||0), 0)
  })).filter(c => c.total > 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <button onClick={() => navigate("/trips")} className="btn btn-secondary btn-sm">← Back</button>
        <div style={{ flex:1 }}>
          <h1 className="page-title">{trip.title}</h1>
          <p className="page-subtitle">{trip.purpose} {trip.destination && `• ${trip.destination}`}</p>
        </div>
        <span className={`badge badge-${trip.status}`} style={{ fontSize:13, padding:"6px 14px" }}>{trip.status}</span>
      </div>

      {/* Keep the server-confirmed entry count visible before the employee
          reviews totals or adds another bill. This makes missing/duplicate
          saves easy to spot while the trip is still being prepared. */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
        padding: window.innerWidth <= 768 ? "12px 14px" : "14px 18px",
        marginBottom:20, borderRadius:12, background:"#EEF2FF",
        border:"1px solid #C7D2FE", color:"#312E81", flexWrap:"wrap"
      }}>
        <div>
          <div style={{ fontSize:12, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.05em" }}>
            Trip entries added
          </div>
          <div style={{ fontSize:12, color:"#4F46E5", marginTop:2 }}>
            Cross-check this count after saving each bill or cash voucher.
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginLeft:"auto" }}>
          <div style={{ fontSize:24, fontWeight:900, minWidth:42, textAlign:"center" }}>
            {expenses.length}
          </div>
          <button
            type="button"
            disabled={expenses.length === 0}
            onClick={downloadLatestTripExcel}
            style={{
              border:"1px solid #818CF8", borderRadius:8, padding:"8px 12px",
              background:expenses.length ? "white" : "#E5E7EB",
              color:expenses.length ? "#3730A3" : "#9CA3AF",
              fontWeight:800, cursor:expenses.length ? "pointer" : "not-allowed"
            }}
          >
            📥 Download Entries Excel
          </button>
        </div>
      </div>

      {/* ── Correction Banner ── */}
      {trip.status === "correction" && (() => {
        const correctionExps = expenses.filter(e => e.bill_correction_requested);
        return (
          <div style={{
            padding:"18px 24px", borderRadius:12, marginBottom:20,
            background:"linear-gradient(135deg,#FFFBEB,#FEF3C7)",
            border:"2px solid #FDE68A"
          }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
              <span style={{ fontSize:28 }}>⚠️</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:16, fontWeight:800, color:"#92400e", marginBottom:4 }}>
                  Bill Correction Required
                </div>
                <div style={{ fontSize:14, color:"#78350f", marginBottom:12 }}>
                  Finance has requested corrections on {correctionExps.length > 1 ? `${correctionExps.length} bills` : "a bill"} in this trip.
                  Please delete the incorrect bill(s) below, upload the correct ones, then resubmit.
                </div>
                {correctionExps.length > 0 && (
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {correctionExps.map(exp => (
                      <div key={exp.id} style={{ background:"rgba(255,255,255,0.7)", borderRadius:8, padding:"8px 12px", fontSize:13 }}>
                        <span style={{ fontWeight:700, color:"#92400e" }}>{exp.category}</span>
                        {exp.description && <span style={{ color:"#78350f" }}> — {exp.description}</span>}
                        {exp.bill_correction_note && (
                          <div style={{ color:"#D97706", fontStyle:"italic", marginTop:2 }}>
                            📝 {exp.bill_correction_note}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {(trip.status === "submitted" || trip.status === "settled") && (
        <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:12, padding:"14px 18px", marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
          <div style={{ fontSize:14, color:"#1E3A8A", lineHeight:1.5, flex:"1 1 260px" }}>
            <strong>This trip is {trip.status === "settled" ? "settled" : "submitted"} and closed.</strong> New bills can't be added to it.
            To add a new bill, start a new trip by requesting an advance. Any balance you carry forward is added to the new advance.
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate("/dashboard?requestAdvance=1")}>💰 Request advance for a new trip</button>
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:16, marginBottom:24 }}>
        {[
          { label:"Advance", value:`₹${parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}`, color:"#d97706", bg:"#FEF3C7", icon:"💰" },
          { label:"Total Spent", value:`₹${parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}`, color:"#E31E24", bg:"#FEE2E2", icon:"💸" },
          { label:"Balance", value:`${balance>=0?"+":""}₹${Math.abs(balance).toLocaleString("en-IN")}`, color:balance>=0?"#059669":"#DC2626", bg:balance>=0?"#D1FAE5":"#FEE2E2", icon:balance>=0?"✅":"⚠️" },
          { label:"Total Entries", value:expenses.length, color:"#4f46e5", bg:"#EEF2FF", icon:"📋" },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:"16px 20px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:s.color, opacity:0.7, textTransform:"uppercase", letterSpacing:"0.05em" }}>{s.label}</div>
            <div style={{ fontSize: window.innerWidth <= 768 ? 18 : 22, fontWeight:800, color:s.color, marginTop:4 }}>{s.icon} {s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "1fr 280px", gap:20 }}>
        {/* Expenses List */}
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:"20px 24px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <h3 style={{ fontSize:17, fontWeight:700 }}>Expense Entries</h3>
            {trip.status === "active" && (
              <button className="btn btn-primary btn-sm" onClick={openAddExpense}>+ Add Expense</button>
            )}
          </div>

          {expenses.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize:40 }}>📋</div>
              <h3>No expenses yet</h3>
              <p>Add your first expense for this trip</p>
              {trip.status === "active" && <button className="btn btn-primary" style={{ marginTop:12 }} onClick={openAddExpense}>+ Add Expense</button>}
            </div>
          ) : (
            <div>
              {expenses.map(exp => (
                <div key={exp.id} className="expense-entry-row" style={{
                  padding:"16px 24px", borderBottom:"1px solid #f9fafb", display:"flex", alignItems:"flex-start", gap:16,
                  background: exp.bill_correction_requested ? "rgba(251,191,36,0.06)" : "transparent"
                }}>
                  <div style={{ fontSize:28, marginTop:2 }}>{CATEGORY_ICONS[exp.category] || "📌"}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <span style={{ fontWeight:700, color:"#1f2937" }}>
                        {exp.category}
                        {exp.food_subcategory && <span style={{ fontWeight:400, color:"#6b7280" }}> · {exp.food_subcategory}</span>}
                        {exp.hotel_name && <span style={{ fontWeight:400, color:"#6b7280" }}> · {exp.hotel_name}</span>}
                        {exp.cash_voucher_type && <span style={{ fontWeight:400, color:"#6b7280" }}> · {exp.cash_voucher_type}</span>}
                        {exp.print_subcategory && <span style={{ fontWeight:400, color:"#6b7280" }}> · {exp.print_subcategory}</span>}
                        {exp.expense_subcategory && <span style={{ fontWeight:400, color:"#6b7280" }}> · {exp.expense_subcategory}</span>}
                      </span>
                      {exp.breakfast_included && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#D1FAE5", color:"#065F46", fontWeight:600 }}>🍳 BF Included</span>
                      )}
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                        background: exp.bill_type==="cash_voucher" ? "#FEF3C7" : "#D1FAE5",
                        color: exp.bill_type==="cash_voucher" ? "#D97706" : "#065F46",
                        fontWeight:600 }}>
                        {exp.bill_type === "cash_voucher" ? "Cash Voucher" : "Bill"}
                      </span>
                      {exp.bf_override_reason && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#FEF3C7", color:"#92400e", fontWeight:600 }}>
                          ⚠️ BF Override
                        </span>
                      )}
                      {exp.bill_correction_requested && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#FEF3C7", color:"#92400e", fontWeight:700 }}>
                          ⚠️ Fix Required
                        </span>
                      )}
                      {/* Awaiting approval badge — the day's cash vouchers went over ₹500 */}
                      {exp.cash_bill_approval_id && exp.cash_bill_manager_status === "pending" && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#FEF3C7", color:"#B45309", fontWeight:700, display:"flex", alignItems:"center", gap:3 }}>
                          ⏳ Awaiting Approval
                        </span>
                      )}
                      {exp.cash_bill_approval_id && exp.cash_bill_manager_status === "approved" && exp.cash_bill_finance_status === "pending" && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#DBEAFE", color:"#1D4ED8", fontWeight:700, display:"flex", alignItems:"center", gap:3 }}>
                          ✅ Manager Approved · Finance Pending
                        </span>
                      )}
                      {exp.cash_bill_approval_id && exp.cash_bill_finance_status === "approved" && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#D1FAE5", color:"#065F46", fontWeight:700, display:"flex", alignItems:"center", gap:3 }}>
                          ✅ Approved
                        </span>
                      )}
                      {exp.cash_bill_approval_id && (exp.cash_bill_manager_status === "declined" || exp.cash_bill_finance_status === "declined") && (
                        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:"#FEE2E2", color:"#991B1B", fontWeight:700, display:"flex", alignItems:"center", gap:3 }}>
                          ❌ Declined
                        </span>
                      )}
                    </div>
                    {exp.category === "Food" && Number(exp.food_total_people_count) > 1 && (
                      <div style={{ marginTop:6, fontSize:12, color:"#475569", lineHeight:1.5 }}>
                        {exp.food_pack_count ? `${exp.food_pack_count} food packs · ` : ""}
                        {exp.food_total_people_count} people · ₹{Number(exp.food_per_person_amount || 0).toLocaleString("en-IN", { minimumFractionDigits:2 })} per person
                        {Number(exp.food_external_people_count) > 0 && ` · ${exp.food_external_people_count} external`}
                      </div>
                    )}
                    <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>{exp.description}</div>
                    <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>
                      📅 {new Date(exp.expense_date).toLocaleDateString("en-IN")}
                    </div>
                    {/* Show route for Travel→Cab/Auto */}
                    {(exp.travel_from || exp.travel_to) && (
                      <div style={{ fontSize:12, color:"#374151", marginTop:4, display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
                        <span>📍</span>
                        <span style={{ fontWeight:600 }}>{exp.travel_from || "?"}</span>
                        <span style={{ color:"#9ca3af" }}>→</span>
                        <span style={{ fontWeight:600 }}>{exp.travel_to || "?"}</span>
                        {exp.taxi_stops && (() => {
                          try {
                            const stops = JSON.parse(exp.taxi_stops);
                            if (stops?.length) return (
                              <span style={{ fontSize:11, padding:"1px 6px", borderRadius:8, background:"#F3F4F6", color:"#6b7280" }}>
                                +{stops.length} stop{stops.length > 1 ? "s" : ""}
                              </span>
                            );
                          } catch { return null; }
                          return null;
                        })()}
                      </div>
                    )}
                    {exp.bill_correction_requested && exp.bill_correction_note && (
                      <div style={{ marginTop:6, padding:"6px 10px", background:"#FEF3C7", borderRadius:8, fontSize:13, color:"#92400e", fontStyle:"italic" }}>
                        📝 Finance note: {exp.bill_correction_note}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:18, fontWeight:800, color:"#E31E24" }}>
                      ₹{parseFloat(exp.amount).toLocaleString("en-IN")}
                    </div>
                    {exp.bill_url && (
                      <button onClick={() => handleViewBill(exp)}
                        style={{ fontSize:12, color:"#2AB5A5", background:"none", border:"none", cursor:"pointer", fontWeight:600, padding:0 }}>
                        🖼️ View Bill
                      </button>
                    )}
                    {trip.status !== "settled" && (
                      <div style={{ display:"flex", gap:6, marginTop:6, justifyContent:"flex-end", flexWrap:"wrap" }}>
                        {exp.bill_url && (
                          <button
                            className="btn btn-danger btn-sm"
                            style={{ fontSize:11, padding:"2px 8px" }}
                            disabled={deletingBillId === exp.id}
                            onClick={() => handleDeleteBill(exp.id)}
                            title="Delete bill">
                            {deletingBillId === exp.id ? "..." : "🗑️ Bill"}
                          </button>
                        )}
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ fontSize:11, padding:"2px 8px" }}
                          onClick={() => { setReuploadExpId(exp.id); reuploadInputRef.current.click(); }}
                          title={exp.bill_url ? "Replace bill" : "Upload bill"}>
                          📎 {exp.bill_url ? "Replace" : "Upload"}
                        </button>
                        {exp.bill_url && (
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize:11, padding:"2px 8px", background:"#ede9fe", color:"#5b21b6", border:"1px solid #c4b5fd" }}
                            onClick={() => { setAddPageExpId(exp.id); addPageInputRef.current.click(); }}
                            title="Add another page to this bill">
                            ➕ Add Page
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="expense-entry-primary-actions">
                    {trip.status === "active" && (
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(exp.id)} title="Delete expense">🗑️</button>
                    )}
                    {trip.status !== "settled" && (
                      <button className="btn btn-secondary btn-sm" onClick={() => startEditExpense(exp)} title="Edit expense details">✏️ Edit</button>
                    )}
                    {canAskQuery && (
                      <button
                        onClick={() => { setTdQueryModal({ expenseId: exp.id, label: `${exp.category} · ₹${parseFloat(exp.amount).toLocaleString("en-IN")} · ${new Date(exp.expense_date).toLocaleDateString("en-IN")}` }); setTdQueryText(""); }}
                        style={{ padding:"3px 8px", borderRadius:6, border:"1px solid rgba(99,102,241,0.4)", background:"rgba(99,102,241,0.08)", color:"#6366f1", fontSize:11, cursor:"pointer", fontWeight:600, whiteSpace:"nowrap" }}
                        title="Ask employee a question about this expense">
                        ❓ Query
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {byCat.length > 0 && (
            <div className="card">
              <h4 style={{ fontWeight:700, marginBottom:16, color:"#1f2937" }}>Category Breakdown</h4>
              {byCat.map(c => (
                <div key={c.cat} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span>{CATEGORY_ICONS[c.cat]}</span>
                    <span style={{ fontSize:13, color:"#374151" }}>{c.cat}</span>
                  </div>
                  <span style={{ fontSize:14, fontWeight:700, color:"#E31E24" }}>
                    ₹{c.total.toLocaleString("en-IN")}
                  </span>
                </div>
              ))}
            </div>
          )}

          {trip.status === "active" && expenses.length > 0 && (
            <div className="card" style={{ background:"linear-gradient(135deg,#E31E24,#B71C1C)", border:"none" }}>
              <h4 style={{ fontWeight:700, color:"white", marginBottom:8 }}>Ready to Submit?</h4>
              <p style={{ fontSize:13, color:"rgba(255,255,255,0.8)", marginBottom:16 }}>
                {submissionReadiness.can_submit
                  ? "All queries are resolved and required manager reviews are approved."
                  : submissionReadiness.blockers?.[0] || "Complete the outstanding review before submitting."}
              </p>
              {!submissionReadiness.can_submit && submissionReadiness.blockers?.length > 1 && (
                <ul style={{ color:"white", fontSize:12, margin:"-6px 0 14px", paddingLeft:18 }}>
                  {submissionReadiness.blockers.slice(1).map(item => <li key={item}>{item}</li>)}
                </ul>
              )}
              {overspendAmount > 0.005 && (
                <div style={{ background:"rgba(255,255,255,.96)", color:"#1f2937", borderRadius:12, padding:14, marginBottom:14 }}>
                  <div style={{ fontWeight:800, color:"#991b1b", marginBottom:4 }}>Company owes you ₹{overspendAmount.toLocaleString("en-IN", { minimumFractionDigits:2 })}</div>
                  <div style={{ fontSize:12, color:"#4b5563", marginBottom:10 }}>Choose one route. Finance must verify it before the financial settlement is complete.</div>
                  <div style={{ display:"grid", gap:8 }}>
                    {[
                      ["reimbursement", "Reimburse this trip", "Finance pays the excess and records the payment reference."],
                      ["next_advance", "Add it to my next advance", "Close this trip after Finance review; pay this amount in addition to the next trip's usable advance."],
                      ["supplementary_advance", "Request an additional advance", "Send the exact excess amount through manager approval for this same trip."],
                    ].map(([value,label,help]) => (
                      <label key={value} style={{ display:"flex", gap:9, alignItems:"flex-start", padding:"9px 10px", border:`1.5px solid ${overspendResolution === value ? "#E31E24" : "#e5e7eb"}`, borderRadius:9, cursor:"pointer", background:overspendResolution === value ? "#fff1f2" : "white" }}>
                        <input type="radio" name="overspendResolution" value={value} checked={overspendResolution === value} onChange={e=>setOverspendResolution(e.target.value)} style={{ marginTop:3 }} />
                        <span><strong style={{ display:"block", fontSize:13 }}>{label}</strong><span style={{ display:"block", color:"#6b7280", fontSize:11, marginTop:2, lineHeight:1.4 }}>{help}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <button className="btn" onClick={handleSubmitTrip} disabled={!submissionReadiness.can_submit || (overspendAmount > 0.005 && !overspendResolution)}
                style={{ background:"white", color:"#E31E24", width:"100%", justifyContent:"center", fontWeight:700,
                  opacity:submissionReadiness.can_submit && (overspendAmount <= 0.005 || overspendResolution) ? 1 : .65, cursor:submissionReadiness.can_submit && (overspendAmount <= 0.005 || overspendResolution) ? "pointer" : "not-allowed" }}>
                📤 Submit Trip
              </button>
            </div>
          )}

          {trip.status !== "active" && expenses.length > 0 && (
            <div className="card" style={{ background:"linear-gradient(135deg,#0f766e,#0d9488)", border:"none" }}>
              <h4 style={{ fontWeight:700, color:"white", marginBottom:8 }}>Your Trip Statement</h4>
              <p style={{ fontSize:13, color:"rgba(255,255,255,0.82)", marginBottom:16 }}>
                Download the submitted trip, itemised expenses, company-paid costs and totals as an Excel file.
              </p>
              <button
                className="btn"
                type="button"
                onClick={downloadLatestTripExcel}
                style={{ background:"white", color:"#0f766e", width:"100%", justifyContent:"center", fontWeight:700 }}
              >
                Download Trip Expenses (Excel)
              </button>
            </div>
          )}

          {trip.status === "submitted" && overspendAmount > 0.005 && !trip.overspend_resolution && (
            <div className="card" style={{ border:"1.5px solid #fca5a5", background:"#fff7f7" }}>
              <h4 style={{ fontWeight:800, color:"#991b1b", marginBottom:5 }}>
                Choose excess-expense settlement
              </h4>
              <p style={{ fontSize:13, color:"#4b5563", marginBottom:12 }}>
                Company owes you <strong>₹{overspendAmount.toLocaleString("en-IN", { minimumFractionDigits:2 })}</strong>.
                This trip was submitted before a settlement route was selected.
              </p>
              <div style={{ display:"grid", gap:8, marginBottom:12 }}>
                {[
                  ["reimbursement", "Direct reimbursement", "Finance pays the excess for this trip."],
                  ["next_advance", "Carry excess into the next advance", "Finance adds this amount to your next bank credit."],
                  ["supplementary_advance", "Request supplementary advance for this trip", "Send the excess through approval and keep this trip open until credited."],
                ].map(([value,label,help]) => (
                  <label key={value} style={{ display:"flex", gap:9, padding:"9px 10px", borderRadius:9,
                    border:`1.5px solid ${overspendResolution === value ? "#E31E24" : "#e5e7eb"}`,
                    background:overspendResolution === value ? "#fff1f2" : "white", cursor:"pointer" }}>
                    <input type="radio" name="legacyOverspendResolution" value={value}
                      checked={overspendResolution === value} onChange={e=>setOverspendResolution(e.target.value)} />
                    <span><strong style={{ display:"block", fontSize:13 }}>{label}</strong>
                      <span style={{ display:"block", color:"#6b7280", fontSize:11, marginTop:2 }}>{help}</span></span>
                  </label>
                ))}
              </div>
              <button type="button" className="btn btn-primary" style={{ width:"100%", justifyContent:"center" }}
                disabled={!overspendResolution || savingOverspendResolution}
                onClick={saveLegacyOverspendResolution}>
                {savingOverspendResolution ? "Saving..." : "Save settlement option"}
              </button>
            </div>
          )}

          {trip.status === "submitted" && balance > 0 && (
            <div className="card" style={{ border:"1.5px solid #f59e0b", background:"#fffbeb" }}>
              <h4 style={{ fontWeight:800, color:"#92400e", marginBottom:6 }}>Return unused advance</h4>
              <p style={{ fontSize:13, color:"#78350f", marginBottom:12 }}>
                Unused advance: <strong>₹{balance.toLocaleString("en-IN", { minimumFractionDigits:2 })}</strong>.
                A return reduces this balance only after Finance verifies the payment proof.
              </p>
              {advanceReturns.map(item => (
                <div key={item.id} style={{ padding:"10px 12px", borderRadius:8, background:"white", border:"1px solid #fde68a", marginBottom:8, fontSize:12 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", gap:8, fontWeight:700, color:"#374151" }}>
                    <span>₹{Number(item.submitted_amount).toLocaleString("en-IN")} · {item.payment_reference}</span>
                    <span style={{ textTransform:"capitalize", color:item.status === "verified" ? "#15803d" : item.status === "rejected" ? "#b91c1c" : "#b45309" }}>{item.status}</span>
                  </div>
                  {item.finance_note && <div style={{ color:"#4b5563", marginTop:5 }}>Finance: {item.finance_note}</div>}
                </div>
              ))}
              {returnOutstanding > 0 && !advanceReturns.some(r => ["pending","queried"].includes(r.status)) && (
                <form onSubmit={submitAdvanceReturn} style={{ display:"grid", gap:10, marginTop:12 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10 }}>
                    <label className="form-group" style={{ margin:0 }}><span className="form-label">Amount returned *</span><input className="form-control" type="number" min="0.01" max={returnOutstanding} step="0.01" required value={returnForm.amount} onChange={e=>setReturnForm(f=>({...f,amount:e.target.value}))} placeholder={returnOutstanding.toFixed(2)} /></label>
                    <label className="form-group" style={{ margin:0 }}><span className="form-label">Method *</span><select className="form-control" value={returnForm.payment_method} onChange={e=>setReturnForm(f=>({...f,payment_method:e.target.value}))}><option value="upi">UPI</option><option value="bank_transfer">Bank transfer</option><option value="cash">Cash</option></select></label>
                  </div>
                  <label className="form-group" style={{ margin:0 }}><span className="form-label">Payment reference *</span><input className="form-control" required minLength="4" value={returnForm.payment_reference} onChange={e=>setReturnForm(f=>({...f,payment_reference:e.target.value}))} placeholder="UPI transaction / UTR / Finance cash receipt number" /></label>
                  <label className="form-group" style={{ margin:0 }}><span className="form-label">Payment date and time *</span><input className="form-control" type="datetime-local" required value={returnForm.payment_date} onChange={e=>setReturnForm(f=>({...f,payment_date:e.target.value}))} /></label>
                  <label className="form-group" style={{ margin:0 }}><span className="form-label">Payment proof *</span><input className="form-control" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" required onChange={e=>setReturnForm(f=>({...f,proof:e.target.files?.[0] || null}))} /></label>
                  <label className="form-group" style={{ margin:0 }}><span className="form-label">Note (optional)</span><input className="form-control" value={returnForm.note} onChange={e=>setReturnForm(f=>({...f,note:e.target.value}))} /></label>
                  <button className="btn btn-primary" disabled={returnSaving}>{returnSaving ? "Submitting…" : "Submit return to Finance"}</button>
                </form>
              )}
              {returnOutstanding <= 0 && <div style={{ color:"#15803d", fontWeight:700, marginTop:8 }}>The full unused advance has been returned or is awaiting verification.</div>}
            </div>
          )}

          {trip.status === "correction" && (() => {
            const pendingFixes = expenses.filter(e => e.bill_correction_requested);
            const allFixed = pendingFixes.length === 0;
            return (
              <div className="card" style={{ background:"linear-gradient(135deg,#D97706,#92400e)", border:"none" }}>
                <h4 style={{ fontWeight:700, color:"white", marginBottom:8 }}>Bills Corrected?</h4>
                <p style={{ fontSize:13, color:"rgba(255,255,255,0.8)", marginBottom:8 }}>
                  {allFixed
                    ? "All corrections done! Resubmit for finance review."
                    : `${pendingFixes.length} bill${pendingFixes.length > 1 ? "s" : ""} still flagged — fix them above first.`}
                </p>
                {!allFixed && (
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginBottom:12 }}>
                    Delete the flagged bill and upload the correct one, then resubmit.
                  </div>
                )}
                <button className="btn" onClick={handleResubmitTrip} disabled={!allFixed}
                  style={{ background:"white", color:"#D97706", width:"100%", justifyContent:"center", fontWeight:700,
                    opacity: allFixed ? 1 : 0.5, cursor: allFixed ? "pointer" : "not-allowed" }}>
                  🔄 Resubmit Trip
                </button>
              </div>
            );
          })()}

          <div className="card">
            <h4 style={{ fontWeight:700, marginBottom:16, color:"#1f2937" }}>Trip Details</h4>
            {[
              { label:"Status", value:<span className={`badge badge-${trip.status}`}>{trip.status}</span> },
              { label:"Start Date", value:new Date(trip.start_date).toLocaleDateString("en-IN") },
              { label:"End Date", value:trip.end_date ? new Date(trip.end_date).toLocaleDateString("en-IN") : "—" },
              { label:"Destination", value:trip.destination || "—" },
            ].map(d => (
              <div key={d.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:10, fontSize:13 }}>
                <span style={{ color:"#9ca3af", fontWeight:600 }}>{d.label}</span>
                <span style={{ color:"#374151", fontWeight:600 }}>{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hidden file input for bill reupload */}
      <input
        type="file"
        accept="image/*,application/pdf"
        style={{ display:"none" }}
        ref={reuploadInputRef}
        onChange={handleReuploadBillFile}
      />
      {/* Hidden file input for adding a bill page */}
      <input
        type="file"
        accept="image/*,application/pdf"
        style={{ display:"none" }}
        ref={addPageInputRef}
        onChange={handleAddBillPage}
      />

      {/* ══ Add Expense Modal ══ */}
      {showModal && (
        <div className="modal-overlay expense-modal-overlay" onClick={closeExpenseModal}>
          <div ref={expenseModalRef} className="modal expense-editor-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize:18, fontWeight:700 }}>{editingExpense ? "✏️ Edit Expense" : "+ Add Expense"}</h2>
              <button onClick={closeExpenseModal} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <form id="add-expense-form" onSubmit={handleSubmitExpense}>
              <div className="modal-body">
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input type="date" className="form-control"
                      value={form.expense_date} onChange={e => setForm(f => ({...f, expense_date:e.target.value}))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select className="form-control" value={form.category} onChange={e => handleCategoryChange(e.target.value)}>
                      {CATEGORIES.map(c => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                      {isOetTrip && (
                        <optgroup label="OET Expenses Module">
                          {OET_SPECIAL_CATEGORIES.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </optgroup>
                      )}
                      {isEbekmed && (
                        <optgroup label="Ebekmed Categories">
                          {EBEKMED_EXTRA_CATEGORIES.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </optgroup>
                      )}
                      {trip?.title === "Petty Cash" && (
                        <optgroup label="Petty Cash Expenses">
                          {PETTY_CASH_CATEGORIES.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {hotelCompanyPaidExists && (
                      <div style={{ marginTop:6, fontSize:12, color:"#1e40af" }}>
                        🏢 A company-paid hotel cost is already recorded. Additional hotel entries remain available.
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Food Subcategory ── */}
                {isOetModuleCategory && (
                  <div className="form-group" style={{ border:"1px solid #E5E7EB", borderRadius:10, padding:"12px 14px", background:"#F9FAFB" }}>
                    <label className="form-label">OET Payment Details</label>
                    {["Interlocutor Fee", "Interlocutor Conveyance"].includes(form.category) && (
                      <div style={{ marginBottom:12 }}>
                        <label className="form-label">Total no. of Interlocutors</label>
                        <input className="form-control" type="number" min="0" value={form.oet_interlocutor_count}
                          onChange={e => setForm(f => ({ ...f, oet_interlocutor_count:e.target.value, oet_interlocutor_names:buildNameFields(e.target.value) }))} />
                        {form.oet_interlocutor_names.map((name, index) => (
                          <input key={index} className="form-control" style={{ marginTop:8 }} placeholder={`Interlocutor ${index + 1} name`} value={name}
                            onChange={e => setForm(f => {
                              const names = [...f.oet_interlocutor_names];
                              names[index] = e.target.value;
                              return { ...f, oet_interlocutor_names:names };
                            })} />
                        ))}
                      </div>
                    )}
                    {["Helper Charges", "Invigilator Fee"].includes(form.category) && (
                      <div>
                        <label className="form-label">Total no. of Persons *</label>
                        <input className="form-control" type="number" min="0" value={form.oet_freelancer_count}
                          onChange={e => setForm(f => ({ ...f, oet_freelancer_count:e.target.value, oet_freelancer_names:buildNameFields(e.target.value) }))} />
                        {form.oet_freelancer_names.map((name, index) => (
                          <input key={index} className="form-control" style={{ marginTop:8 }} placeholder={`Person ${index + 1} name *`} value={name}
                            onChange={e => setForm(f => {
                              const names = [...f.oet_freelancer_names];
                              names[index] = e.target.value;
                              return { ...f, oet_freelancer_names:names };
                            })} />
                        ))}
                      </div>
                    )}
                    {form.category === "Support Staff" && (
                      <div>
                        <label className="form-label">No. of Supporting Staff</label>
                        <input className="form-control" type="number" min="0" value={form.oet_support_staff_count}
                          onChange={e => setForm(f => ({ ...f, oet_support_staff_count:e.target.value, oet_support_staff_names:buildNameFields(e.target.value) }))} />
                        {form.oet_support_staff_names.map((name, index) => (
                          <input key={index} className="form-control" style={{ marginTop:8 }} placeholder={`Support Staff ${index + 1} name`} value={name}
                            onChange={e => setForm(f => {
                              const names = [...f.oet_support_staff_names];
                              names[index] = e.target.value;
                              return { ...f, oet_support_staff_names:names };
                            })} />
                        ))}
                      </div>
                    )}
                    {form.category === "Catering Charges" && (
                      <div style={{ display:"grid", gap:12 }}>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10 }}>
                          {[
                            ["catering_candidate_count", "Total Candidates"],
                            ["catering_ebek_staff_count", "Ebek Staff"],
                            ["catering_support_staff_count", "Support Staff"],
                          ].map(([field, label]) => (
                            <div key={field}>
                              <label className="form-label">{label} *</label>
                              <input className="form-control" type="number" min="0" value={form[field]}
                                onChange={e => setForm(f => ({ ...f, [field]:e.target.value }))} required />
                            </div>
                          ))}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10 }}>
                          {[
                            ["catering_bf_packs", "Breakfast Packs"],
                            ["catering_lunch_packs", "Lunch Packs"],
                            ["catering_snacks_packs", "Snacks Packs"],
                          ].map(([field, label]) => (
                            <div key={field}>
                              <label className="form-label">{label} *</label>
                              <input className="form-control" type="number" min="0" value={form[field]}
                                onChange={e => setForm(f => ({ ...f, [field]:e.target.value }))} required />
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize:12, color:"#475569" }}>
                          Enter 0 where a headcount or meal was not applicable. Description, amount and proof are required below.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {form.category === "Food" && (

                  <div className="form-group">
                    <label className="form-label">Food Type *</label>
                    {blockedFoodSubtypes.length > 0 && (
                      <div style={{ marginBottom:8, padding:"10px 14px", background:"#FEF3C7", border:"1.5px solid #FDE68A", borderRadius:10, fontSize:13, color:"#92400e" }}>
                        🚫 <strong>{blockedFoodSubtypes.join(", ")}</strong> already covered — you were included in a shared expense by a teammate. You can still claim other food types.
                      </div>
                    )}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                      {FOOD_SUBCATEGORIES.map(sub => {
                        const isBf = sub === "BF";
                        const bfBlocked = isBf && hotelHasBf;
                        const foodSubBlocked = blockedFoodSubtypes.includes(sub);
                        const fullyBlocked = foodSubBlocked || bfBlocked;
                        return (
                          <div
                            key={sub}
                            onClick={() => !fullyBlocked && setForm(f => ({...f, food_subcategory: sub, bf_override_reason:""}))}
                            style={{
                              border:`2px solid ${form.food_subcategory===sub?"#E31E24":fullyBlocked?"#d1d5db":"#e5e7eb"}`,
                              borderRadius:8, padding:"8px 10px", cursor: fullyBlocked ? "not-allowed" : "pointer",
                              background: form.food_subcategory===sub ? "#FFEBEE" : fullyBlocked ? "#f3f4f6" : "white",
                              textAlign:"center", fontSize:13, fontWeight:600,
                              color: form.food_subcategory===sub ? "#E31E24" : fullyBlocked ? "#9ca3af" : "#374151",
                              opacity: fullyBlocked ? 0.55 : 1,
                              position:"relative",
                            }}>
                            {sub}
                            {bfBlocked && !foodSubBlocked && <div style={{ fontSize:9, color:"#DC2626", marginTop:2 }}>Hotel BF ✗</div>}
                            {foodSubBlocked && <div style={{ fontSize:9, color:"#9ca3af", marginTop:2 }}>Shared ✗</div>}
                          </div>
                        );
                      })}
                    </div>
                    {/* BF blocked info — tapping BF when hotel has BF included */}
                    {hotelHasBf && form.food_subcategory !== "BF" && (
                      <div style={{ marginTop:8, padding:"8px 12px", background:"#FEF3C7", borderRadius:8, fontSize:12, color:"#92400e" }}>
                        🍳 Hotel breakfast is included in your trip. BF requires override approval.
                        <span
                          onClick={() => setForm(f => ({...f, food_subcategory:"BF"}))}
                          style={{ marginLeft:6, color:"#D97706", fontWeight:700, cursor:"pointer", textDecoration:"underline" }}>
                          Override anyway →
                        </span>
                      </div>
                    )}
                    {/* BF override reason field */}
                    {form.food_subcategory === "BF" && hotelHasBf && (
                      <div style={{ marginTop:10, padding:"12px 14px", background:"#FFF7ED", border:"2px solid #FDE68A", borderRadius:10 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#92400e", marginBottom:8 }}>
                          ⚠️ Why are you claiming BF separately? (Manager approval required)
                        </div>
                        <select
                          className="form-control"
                          value={form.bf_override_reason}
                          onChange={e => setForm(f => ({...f, bf_override_reason:e.target.value}))}>
                          <option value="">— Select reason —</option>
                          {BF_OVERRIDE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Hotel Fields ── */}
                {form.category === "Hotel" && (
                  <div className="form-group">
                    <label className="form-label">Hotel Name</label>
                    <input
                      className="form-control"
                      placeholder="e.g. Hotel Raj Palace"
                      value={form.hotel_name}
                      onChange={e => setForm(f => ({...f, hotel_name:e.target.value}))}
                    />
                    <label className="form-label" style={{ marginTop:10 }}>Hotel Location *</label>
                    <input className="form-control" placeholder="City / locality"
                      value={form.hotel_location}
                      onChange={e => setForm(f => ({...f, hotel_location:e.target.value}))} />
                    {/* Company Paid checkbox */}
                    <div
                      style={{ marginTop:12, display:"flex", alignItems:"center", gap:10, cursor:"pointer",
                        padding:"10px 12px", border:"1.5px solid #e5e7eb", borderRadius:8,
                        background: form.hotel_company_paid ? "#EFF6FF" : "#fafafa" }}
                      onClick={() => setForm(f => ({...f, hotel_company_paid:!f.hotel_company_paid}))}>
                      <input
                        type="checkbox"
                        readOnly
                        checked={form.hotel_company_paid}
                        style={{ width:16, height:16, cursor:"pointer", accentColor:"#2563eb" }}
                      />
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:"#1e40af" }}>🏢 Bills paid by company</div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:1 }}>
                          {form.hotel_company_paid
                            ? "Hotel/Stay will be excluded from your claimable expenses"
                            : "Check if the hotel bill was settled directly by the company"}
                        </div>
                      </div>
                    </div>
                    {form.hotel_company_paid && (
                      <div style={{ marginTop:8, padding:"8px 12px", background:"#DBEAFE", borderRadius:8, fontSize:12, color:"#1e40af" }}>
                        Enter the booking amount below. It will count toward the trip total but not your reimbursement.
                      </div>
                    )}
                    {/* Breakfast Included Toggle */}
                    {!form.hotel_company_paid && (
                      <>
                        <div style={{ marginTop:12, display:"flex", alignItems:"center", gap:12 }}>
                          <div
                            onClick={() => setForm(f => ({...f, breakfast_included:!f.breakfast_included}))}
                            style={{
                              width:44, height:24, borderRadius:12, cursor:"pointer", transition:"background 0.2s",
                              background:form.breakfast_included ? "#059669" : "#d1d5db",
                              position:"relative", flexShrink:0
                            }}>
                            <div style={{
                              position:"absolute", top:3, left: form.breakfast_included ? 22 : 3,
                              width:18, height:18, borderRadius:"50%", background:"white",
                              transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)"
                            }} />
                          </div>
                          <div>
                            <div style={{ fontSize:13, fontWeight:700, color:"#1f2937" }}>
                              🍳 Breakfast Included in hotel bill?
                            </div>
                            <div style={{ fontSize:11, color:"#6b7280", marginTop:1 }}>
                              {form.breakfast_included
                                ? "BF will be blocked in food expenses for this trip"
                                : "Turn on if hotel bill includes breakfast cost"}
                            </div>
                          </div>
                        </div>
                        {form.breakfast_included && (
                          <div style={{ marginTop:8, padding:"8px 12px", background:"#D1FAE5", borderRadius:8, fontSize:12, color:"#065F46" }}>
                            ✅ BF (breakfast) entries will require override approval for this trip.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Travel Sub-type ── */}
                {showClaimFields && (
                  <>
                {form.category === "Travel" && (
                  <div className="form-group">
                    <label className="form-label">Mode of Travel *</label>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                      {TRAVEL_SUBTYPES.map(sub => (
                        <div key={sub} onClick={() => setForm(f => ({...f, travel_subcategory:sub, travel_booked_by:"", travel_from:"", travel_to:"", travel_class:"", cab_type:"", cab_charge_type:"", cab_kms_travelled:"", cab_total_hours:"", cab_institutions_visited:"", multiple_stops:false, extra_stops:[]}))}
                          style={{
                            border:`2px solid ${form.travel_subcategory===sub?"#E31E24":"#e5e7eb"}`,
                            borderRadius:8, padding:"8px 10px", cursor:"pointer",
                            background:form.travel_subcategory===sub?"#FFEBEE":"white",
                            textAlign:"center", fontSize:13, fontWeight:600,
                            color:form.travel_subcategory===sub?"#E31E24":"#374151"
                          }}>
                          {sub === "Train" ? "🚂" : sub === "Cab" ? "🚕" : sub === "Auto" ? "🛺" : sub === "Bike Taxi" ? "🏍️" : "🚗"} {sub}
                        </div>
                      ))}
                    </div>
                    {/* ── Train: booked-by → then from/to/class or block ── */}
                    {["Train", "Bus", "Flight"].includes(form.travel_subcategory) && (
                      <div style={{ marginTop:12, padding:"12px 14px", border:"1px solid #E5E7EB", borderRadius:10, background:"#F9FAFB" }}>
                        <label className="form-label">{form.travel_subcategory} booked by *</label>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                          {[
                            { value:"self",        label:"🎟️ Booked by me" },
                            { value:"centralized", label:"🏢 Centralized team" },
                          ].map(opt => (
                            <button key={opt.value} type="button"
                              onClick={() => setForm(f => ({...f, travel_booked_by:opt.value, bill: opt.value === "centralized" ? null : f.bill, travel_from:"", travel_to:"", travel_class:""}))}
                              style={{
                                padding:"9px 10px", borderRadius:8, fontFamily:"inherit", cursor:"pointer",
                                border:`2px solid ${form.travel_booked_by === opt.value ? "#E31E24" : "#e5e7eb"}`,
                                background:form.travel_booked_by === opt.value ? "#FFEBEE" : "white",
                                color:form.travel_booked_by === opt.value ? "#E31E24" : "#374151",
                                fontWeight:700,
                              }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        {/* Centralized → blocked */}
                        {form.travel_booked_by === "centralized" && (
                          <div style={{ padding:"10px 14px", background:"#FEF3C7", border:"1px solid #FDE68A", borderRadius:8, fontSize:13, color:"#92400e" }}>
                            Company-paid booking: enter the route and amount. No bill is required and it will not be reimbursed to you.
                          </div>
                        )}

                        {/* Self → From / To / Class */}
                        {form.travel_booked_by && (
                          <>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                              <div className="form-group" style={{ marginBottom:0 }}>
                                <label className="form-label">From *</label>
                                <input className="form-control" placeholder="e.g. Chennai Central"
                                  value={form.travel_from} onChange={e => setForm(f => ({...f, travel_from:e.target.value}))} />
                              </div>
                              <div className="form-group" style={{ marginBottom:0 }}>
                                <label className="form-label">To *</label>
                                <input className="form-control" placeholder="e.g. Bangalore City"
                                  value={form.travel_to} onChange={e => setForm(f => ({...f, travel_to:e.target.value}))} />
                              </div>
                            </div>
                            <div className="form-group" style={{ marginBottom:0 }}>
                              <label className="form-label">Class *</label>
                              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:6 }}>
                                {(form.travel_subcategory === "Flight"
                                  ? ["Economy", "Premium Economy", "Business", "First Class"]
                                  : form.travel_subcategory === "Bus"
                                    ? ["Seater", "Semi Sleeper", "Sleeper", "AC Sleeper"]
                                    : ["AC Tier 1","AC Tier 2","AC Tier 3","Sleeper","Chair Car"]).map(cls => (
                                  <button key={cls} type="button"
                                    onClick={() => setForm(f => ({...f, travel_class:cls}))}
                                    style={{
                                      padding:"7px 4px", borderRadius:8, fontFamily:"inherit", cursor:"pointer", fontSize:11, fontWeight:700, textAlign:"center",
                                      border:`2px solid ${form.travel_class===cls?"#E31E24":"#e5e7eb"}`,
                                      background:form.travel_class===cls?"#FFEBEE":"white",
                                      color:form.travel_class===cls?"#E31E24":"#374151",
                                    }}>
                                    {cls === "AC Tier 1" ? "1A" : cls === "AC Tier 2" ? "2A" : cls === "AC Tier 3" ? "3A" : cls}
                                    <div style={{ fontSize:9, fontWeight:400, marginTop:2, color:"inherit" }}>{cls}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* ── Cab: type → charge model → from/to → KMS or Full Day fields ── */}
                    {form.travel_subcategory === "Cab" && (
                      <div style={{ marginTop:12, padding:"12px 14px", border:"1px solid #E5E7EB", borderRadius:10, background:"#F9FAFB" }}>

                        <label className="form-label">Cab payment *</label>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                          {[
                            { value:"self", label:"👤 Paid by me", note:"Included in your claim and trip expense" },
                            { value:"centralized", label:"🏢 Paid by company", note:"Recorded for Finance; excluded from your claim" },
                          ].map(opt => (
                            <button key={opt.value} type="button"
                              onClick={() => setForm(f => ({ ...f, travel_booked_by: opt.value, bill: opt.value === "centralized" ? null : f.bill }))}
                              style={{ padding:"9px 10px", borderRadius:8, fontFamily:"inherit", cursor:"pointer", textAlign:"left",
                                border:`2px solid ${form.travel_booked_by===opt.value?"#E31E24":"#e5e7eb"}`,
                                background:form.travel_booked_by===opt.value?"#FFEBEE":"white",
                                color:form.travel_booked_by===opt.value?"#E31E24":"#374151", fontWeight:700 }}>
                              <div>{opt.label}</div><div style={{ fontSize:10, fontWeight:500, marginTop:3 }}>{opt.note}</div>
                            </button>
                          ))}
                        </div>
                        {isCompanyPaidCabEntry && (
                          <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF3C7", border:"1px solid #FDE68A", borderRadius:8, fontSize:13, color:"#92400e" }}>
                            Company-paid cab: enter the route, cab details and amount. It will appear in the Finance company-paid cab report, but will not reduce your advance or add to your reimbursable trip expenses.
                          </div>
                        )}

                        {/* Step 1: Cab Type */}
                        <label className="form-label">Cab Type *</label>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                          {[
                            { value:"hired_cab", label:"🚕 Hired Cab" },
                            { value:"personal_car", label:"🚗 Personal Car" },
                          ].map(opt => (
                            <button key={opt.value} type="button"
                              onClick={() => setForm(f => ({...f, cab_type:opt.value, cab_charge_type:"", cab_kms_travelled:"", cab_total_hours:"", cab_institutions_visited:""}))}
                              style={{
                                padding:"9px 10px", borderRadius:8, fontFamily:"inherit", cursor:"pointer",
                                border:`2px solid ${form.cab_type===opt.value?"#E31E24":"#e5e7eb"}`,
                                background:form.cab_type===opt.value?"#FFEBEE":"white",
                                color:form.cab_type===opt.value?"#E31E24":"#374151",
                                fontWeight:700,
                              }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        {/* Step 2: Charge Type (shown for both Private and OLA/UBER) */}
                        {form.cab_type && (
                          <>
                            <label className="form-label">Charge Type *</label>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                              {[
                                { value:"kms_based", label:"📏 KMS Based Charge" },
                                { value:"full_day",  label:"📅 Full Day Charge" },
                              ].map(opt => (
                                <button key={opt.value} type="button"
                                  onClick={() => setForm(f => ({...f, cab_charge_type:opt.value, cab_kms_travelled:"", cab_total_hours:"", cab_institutions_visited:""}))}
                                  style={{
                                    padding:"9px 10px", borderRadius:8, fontFamily:"inherit", cursor:"pointer",
                                    border:`2px solid ${form.cab_charge_type===opt.value?"#E31E24":"#e5e7eb"}`,
                                    background:form.cab_charge_type===opt.value?"#FFEBEE":"white",
                                    color:form.cab_charge_type===opt.value?"#E31E24":"#374151",
                                    fontWeight:700,
                                  }}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}

                        {/* Step 3: From / To (always shown) */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                          <div className="form-group" style={{ marginBottom:0 }}>
                            <label className="form-label">From *</label>
                            <input className="form-control" placeholder="e.g. Hotel"
                              value={form.travel_from} onChange={e => setForm(f => ({...f, travel_from:e.target.value}))} />
                          </div>
                          <div className="form-group" style={{ marginBottom:0 }}>
                            <label className="form-label">To *</label>
                            <input className="form-control" placeholder="e.g. Office / Exam Center"
                              value={form.travel_to} onChange={e => setForm(f => ({...f, travel_to:e.target.value}))} />
                          </div>
                        </div>

                        {/* Step 4a: KMS Based — Total KMs Travelled */}
                        {form.cab_charge_type === "kms_based" && (
                          <div className="form-group" style={{ marginBottom:10 }}>
                            <label className="form-label">Total KMs Travelled *</label>
                            <input className="form-control" type="number" min="0" step="0.1" placeholder="e.g. 45.5"
                              value={form.cab_kms_travelled}
                              onChange={e => setForm(f => ({...f, cab_kms_travelled:e.target.value}))} />
                          </div>
                        )}

                        {/* Step 4b: Full Day — Total Hours + Total KMs + No of Institutions */}
                        {form.cab_charge_type === "full_day" && (
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                            <div className="form-group" style={{ marginBottom:0 }}>
                              <label className="form-label">Total Hours Used *</label>
                              <input className="form-control" type="number" min="0" step="0.5" placeholder="e.g. 8"
                                value={form.cab_total_hours}
                                onChange={e => setForm(f => ({...f, cab_total_hours:e.target.value}))} />
                            </div>
                            <div className="form-group" style={{ marginBottom:0 }}>
                              <label className="form-label">Total KMs Covered *</label>
                              <input className="form-control" type="number" min="0" step="0.1" placeholder="e.g. 120"
                                value={form.cab_kms_travelled}
                                onChange={e => setForm(f => ({...f, cab_kms_travelled:e.target.value}))} />
                            </div>
                            <div className="form-group" style={{ marginBottom:0 }}>
                              <label className="form-label">No. of Institutions *</label>
                              <input className="form-control" type="number" min="0" placeholder="e.g. 3"
                                value={form.cab_institutions_visited}
                                onChange={e => setForm(f => ({...f, cab_institutions_visited:e.target.value}))} />
                            </div>
                          </div>
                        )}

                        {/* Multiple Stops */}
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4, marginBottom: form.multiple_stops ? 10 : 0 }}>
                          <input
                            type="checkbox"
                            id="cab_multiple_stops_cb"
                            checked={form.multiple_stops}
                            onChange={e => setForm(f => ({
                              ...f,
                              multiple_stops: e.target.checked,
                              extra_stops: e.target.checked
                                ? (f.extra_stops.length ? f.extra_stops : [{from:"", to:""}])
                                : [],
                            }))}
                            style={{ width:16, height:16, cursor:"pointer", accentColor:"#E31E24" }}
                          />
                          <label htmlFor="cab_multiple_stops_cb" style={{ margin:0, fontSize:13, fontWeight:600, color:"#374151", cursor:"pointer" }}>
                            Multiple stop points
                          </label>
                        </div>
                        {form.multiple_stops && (
                          <div>
                            {form.extra_stops.map((stop, idx) => (
                              <div key={idx} style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, marginBottom:8, alignItems:"center" }}>
                                <input className="form-control" placeholder={`Stop ${idx+1} From`}
                                  value={stop.from}
                                  onChange={e => setForm(f => ({
                                    ...f,
                                    extra_stops: f.extra_stops.map((s, i) => i === idx ? {...s, from: e.target.value} : s)
                                  }))}
                                />
                                <input className="form-control" placeholder={`Stop ${idx+1} To`}
                                  value={stop.to}
                                  onChange={e => setForm(f => ({
                                    ...f,
                                    extra_stops: f.extra_stops.map((s, i) => i === idx ? {...s, to: e.target.value} : s)
                                  }))}
                                />
                                {form.extra_stops.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setForm(f => ({...f, extra_stops: f.extra_stops.filter((_, i) => i !== idx)}))}
                                    style={{ background:"none", border:"1px solid #fca5a5", borderRadius:6, color:"#ef4444", cursor:"pointer", padding:"4px 8px", fontSize:16, fontWeight:700, lineHeight:1 }}>
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => setForm(f => ({...f, extra_stops: [...f.extra_stops, {from:"", to:""}]}))}
                              style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8, border:"2px dashed #E31E24", background:"white", color:"#E31E24", cursor:"pointer", fontWeight:700, fontSize:13, marginTop:2 }}>
                              + Add Stop
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Auto: From / To + Multiple Stops ── */}
                    {form.travel_subcategory === "Auto" && (
                      <div style={{ marginTop:12, padding:"12px 14px", border:"1px solid #E5E7EB", borderRadius:10, background:"#F9FAFB" }}>
                        {/* Primary From / To */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                          <div className="form-group" style={{ marginBottom:0 }}>
                            <label className="form-label">From *</label>
                            <input className="form-control" placeholder="e.g. Hotel"
                              value={form.travel_from} onChange={e => setForm(f => ({...f, travel_from:e.target.value}))} />
                          </div>
                          <div className="form-group" style={{ marginBottom:0 }}>
                            <label className="form-label">To *</label>
                            <input className="form-control" placeholder="e.g. Exam Center"
                              value={form.travel_to} onChange={e => setForm(f => ({...f, travel_to:e.target.value}))} />
                          </div>
                        </div>
                        {/* Multiple stop points toggle */}
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4, marginBottom: form.multiple_stops ? 10 : 0 }}>
                          <input
                            type="checkbox"
                            id="multiple_stops_cb"
                            checked={form.multiple_stops}
                            onChange={e => setForm(f => ({
                              ...f,
                              multiple_stops: e.target.checked,
                              extra_stops: e.target.checked
                                ? (f.extra_stops.length ? f.extra_stops : [{from:"", to:""}])
                                : [],
                            }))}
                            style={{ width:16, height:16, cursor:"pointer", accentColor:"#E31E24" }}
                          />
                          <label htmlFor="multiple_stops_cb" style={{ margin:0, fontSize:13, fontWeight:600, color:"#374151", cursor:"pointer" }}>
                            Multiple stop points
                          </label>
                        </div>
                        {/* Extra stop pairs */}
                        {form.multiple_stops && (
                          <div>
                            {form.extra_stops.map((stop, idx) => (
                              <div key={idx} style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, marginBottom:8, alignItems:"center" }}>
                                <input
                                  className="form-control"
                                  placeholder={`Stop ${idx+1} — From`}
                                  value={stop.from}
                                  onChange={e => setForm(f => ({
                                    ...f,
                                    extra_stops: f.extra_stops.map((s, i) => i === idx ? {...s, from: e.target.value} : s)
                                  }))}
                                />
                                <input
                                  className="form-control"
                                  placeholder={`Stop ${idx+1} — To`}
                                  value={stop.to}
                                  onChange={e => setForm(f => ({
                                    ...f,
                                    extra_stops: f.extra_stops.map((s, i) => i === idx ? {...s, to: e.target.value} : s)
                                  }))}
                                />
                                {form.extra_stops.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setForm(f => ({...f, extra_stops: f.extra_stops.filter((_, i) => i !== idx)}))}
                                    style={{ background:"none", border:"1px solid #fca5a5", borderRadius:6, color:"#ef4444", cursor:"pointer", padding:"4px 8px", fontSize:16, fontWeight:700, lineHeight:1 }}>
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => setForm(f => ({...f, extra_stops: [...f.extra_stops, {from:"", to:""}]}))}
                              style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8, border:"2px dashed #E31E24", background:"white", color:"#E31E24", cursor:"pointer", fontWeight:700, fontSize:13, marginTop:2 }}>
                              + Add Stop
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Courier Sub-type + Fields ── */}
                {form.category === "Travel" && ["Auto", "Bike Taxi", "Others"].includes(form.travel_subcategory) && (
                  <div className="form-group" style={{ padding:"12px 14px", border:"1px solid #E5E7EB", borderRadius:10, background:"#F9FAFB" }}>
                    {["Bike Taxi", "Others"].includes(form.travel_subcategory) && (
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                        <div><label className="form-label">From *</label><input className="form-control" value={form.travel_from} onChange={e=>setForm(f=>({...f,travel_from:e.target.value}))} /></div>
                        <div><label className="form-label">To *</label><input className="form-control" value={form.travel_to} onChange={e=>setForm(f=>({...f,travel_to:e.target.value}))} /></div>
                      </div>
                    )}
                    <label className="form-label">KMS Travelled *</label>
                    <input className="form-control" type="number" min="0.1" step="0.1" value={form.kms_travelled}
                      onChange={e=>setForm(f=>({...f,kms_travelled:e.target.value}))} />
                  </div>
                )}

                {form.category === "Fuel" && (
                  <div className="form-group">
                    <label className="form-label">Fuel Details *</label>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, marginBottom:12 }}>
                      {FUEL_VEHICLE_TYPES.map(type => (
                        <button key={type} type="button" onClick={() => setForm(f => ({...f, fuel_vehicle_type:type}))}
                          style={{
                            padding:"9px 10px", borderRadius:8, fontFamily:"inherit", cursor:"pointer",
                            border:`2px solid ${form.fuel_vehicle_type === type ? "#E31E24" : "#e5e7eb"}`,
                            background:form.fuel_vehicle_type === type ? "#FFEBEE" : "white",
                            color:form.fuel_vehicle_type === type ? "#E31E24" : "#374151",
                            fontWeight:700,
                          }}>
                          {type}
                        </button>
                      ))}
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:12 }}>
                      {FUEL_TYPES.map(type => (
                        <button key={type} type="button" onClick={() => setForm(f => ({...f, fuel_type:type}))}
                          style={{
                            padding:"9px 10px", borderRadius:8, fontFamily:"inherit", cursor:"pointer",
                            border:`2px solid ${form.fuel_type === type ? "#E31E24" : "#e5e7eb"}`,
                            background:form.fuel_type === type ? "#FFEBEE" : "white",
                            color:form.fuel_type === type ? "#E31E24" : "#374151",
                            fontWeight:700,
                          }}>
                          {type}
                        </button>
                      ))}
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div>
                        <label className="form-label">Vehicle No *</label>
                        <input className="form-control" placeholder="e.g. TN01AB1234" value={form.fuel_vehicle_no}
                          onChange={e => setForm(f => ({...f, fuel_vehicle_no:e.target.value}))} />
                      </div>
                      <div>
                        <label className="form-label">No. of Liters (optional)</label>
                        <input className="form-control" type="number" min="0" step="0.01" placeholder="e.g. 12.5" value={form.fuel_liters}
                          onChange={e => setForm(f => ({...f, fuel_liters:e.target.value}))} />
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
                      <div><label className="form-label">Start Odometer KMS *</label><input className="form-control" type="number" min="0" step="0.1" value={form.odometer_start} onChange={e=>setForm(f=>({...f,odometer_start:e.target.value,kms_travelled:Number(f.odometer_end)>Number(e.target.value)?String(Number(f.odometer_end)-Number(e.target.value)):""}))} /></div>
                      <div><label className="form-label">End Odometer KMS *</label><input className="form-control" type="number" min="0" step="0.1" value={form.odometer_end} onChange={e=>setForm(f=>({...f,odometer_end:e.target.value,kms_travelled:Number(e.target.value)>Number(f.odometer_start)?String(Number(e.target.value)-Number(f.odometer_start)):""}))} /></div>
                    </div>
                    <div style={{ marginTop:8, fontWeight:700 }}>KMS Travelled: {form.kms_travelled || "—"}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
                      <div><label className="form-label">Start Odometer Photo *</label><input className="form-control" type="file" accept="image/*" capture="environment" onChange={e=>setForm(f=>({...f,odometer_start_photo:e.target.files?.[0]||null}))} /></div>
                      <div><label className="form-label">End Odometer Photo *</label><input className="form-control" type="file" accept="image/*" capture="environment" onChange={e=>setForm(f=>({...f,odometer_end_photo:e.target.files?.[0]||null}))} /></div>
                    </div>
                  </div>
                )}

                {form.category === "Courier" && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Courier Type *</label>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                        {COURIER_SUBTYPES.map(sub => (
                          <div key={sub} onClick={() => setForm(f => ({...f, courier_subtype:sub}))}
                            style={{
                              border:`2px solid ${form.courier_subtype===sub?"#E31E24":"#e5e7eb"}`,
                              borderRadius:8, padding:"8px 10px", cursor:"pointer",
                              background:form.courier_subtype===sub?"#FFEBEE":"white",
                              textAlign:"center", fontSize:13, fontWeight:600,
                              color:form.courier_subtype===sub?"#E31E24":"#374151"
                            }}>
                            {sub === "Within City" ? "🏙️" : sub === "Other State" ? "📍" : "🌏"} {sub}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div className="form-group">
                        <label className="form-label">Courier Name / Company *</label>
                        <input className="form-control" placeholder="e.g. DTDC, Blue Dart"
                          value={form.courier_name}
                          onChange={e => setForm(f => ({...f, courier_name:e.target.value}))} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Weight (kg) *</label>
                        <input className="form-control" placeholder="e.g. 1.5"
                          value={form.courier_weight}
                          onChange={e => setForm(f => ({...f, courier_weight:e.target.value}))} />
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div className="form-group"><label className="form-label">From Location *</label><input className="form-control" value={form.courier_from} onChange={e=>setForm(f=>({...f,courier_from:e.target.value}))} /></div>
                      <div className="form-group"><label className="form-label">To Location *</label><input className="form-control" value={form.courier_to} onChange={e=>setForm(f=>({...f,courier_to:e.target.value}))} /></div>
                      <div className="form-group"><label className="form-label">Number of Boxes *</label><input className="form-control" type="number" min="1" step="1" value={form.courier_boxes} onChange={e=>setForm(f=>({...f,courier_boxes:e.target.value}))} /></div>
                      <div className="form-group"><label className="form-label">Tracking / Reference Number *</label><input className="form-control" value={form.courier_tracking_ref} onChange={e=>setForm(f=>({...f,courier_tracking_ref:e.target.value}))} /></div>
                    </div>
                  </>
                )}

                {/* ── Print Sub-category ── */}
                {form.category === "Prints" && (
                  <div className="form-group">
                    <label className="form-label">Print Type *</label>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
                      {PRINT_SUBCATEGORIES.map(sub => (
                        <div key={sub} onClick={() => setForm(f => ({...f, print_subcategory:sub}))}
                          style={{
                            border:`2px solid ${form.print_subcategory===sub?"#E31E24":"#e5e7eb"}`,
                            borderRadius:8, padding:"9px 12px", cursor:"pointer",
                            background:form.print_subcategory===sub?"#FFEBEE":"white",
                            textAlign:"center", fontSize:13, fontWeight:600,
                            color:form.print_subcategory===sub?"#E31E24":"#374151"
                          }}>
                          {sub === "Exam Materials" ? "📄" : sub === "Certificates" ? "🏅" : sub === "Company Brochures" ? "📰" : "🖨️"} {sub}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Miscellaneous Category ── */}
                {form.category === "Miscellaneous" && (
                  <div className="form-group">
                    <label className="form-label">Miscellaneous Category *</label>
                    <input className="form-control" placeholder="e.g. Office Supplies, Parking, etc."
                      value={form.misc_category}
                      onChange={e => setForm(f => ({...f, misc_category:e.target.value}))} />
                  </div>
                )}


                {/* ── Ebekmed: Medical Equipment / Consumables / Delivery Charges sub-type ── */}
                {SUBCATEGORY_REQUIRED_CATEGORIES.includes(form.category) && EBEKMED_SUBCATEGORY_OPTIONS[form.category] && (
                  <div className="form-group">
                    <label className="form-label">
                      {form.category === "Medical Equipment"   ? "Equipment Reason"
                       : form.category === "Medical Consumables" ? "Consumable Reason"
                       : "Delivery Type"} *
                    </label>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
                      {EBEKMED_SUBCATEGORY_OPTIONS[form.category].map(sub => (
                        <div key={sub}
                          onClick={() => setForm(f => ({...f, expense_subcategory: sub}))}
                          style={{
                            border:`2px solid ${form.expense_subcategory===sub?"#2AB5A5":"#e5e7eb"}`,
                            borderRadius:8, padding:"9px 12px", cursor:"pointer",
                            background:form.expense_subcategory===sub?"#E6FAF8":"white",
                            textAlign:"center", fontSize:12, fontWeight:600,
                            color:form.expense_subcategory===sub?"#0d7a6e":"#374151",
                            transition:"all 0.15s",
                          }}>
                          {sub}
                        </div>
                      ))}
                    </div>
                    {!form.expense_subcategory && (
                      <div style={{ marginTop:6, fontSize:12, color:"#9ca3af" }}>Select one to continue.</div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <input className="form-control" placeholder="Brief description of this expense"
                    value={form.description} onChange={e => setForm(f => ({...f, description:e.target.value}))} required />
                </div>

                <div className="form-group">
                  <label className="form-label">Amount (₹) *</label>
                  <input type="number" className="form-control" placeholder="0.00" min="0" step="0.01"
                    value={form.amount}
                    onChange={e => {
                      setForm(f => ({...f, amount:e.target.value}));
                      setCashBillWarning(false);
                    }}
                    required />
                  {/* Cash bill option removed — no Cash Bill warning needed */}
                  {/* Cash voucher: the ₹500 cap is on the day's vouchers together */}
                  {isCashVoucherOverLimit && (
                    <div style={{ marginTop:6, padding:"8px 12px", background:"#FEF3C7", borderRadius:8, fontSize:12, color:"#92400e", fontWeight:600 }}>
                      ⚠️ Manager approval required. {oetCategoryLimit
                        ? `${form.category} is ₹${cashVoucherDayTotal.toLocaleString("en-IN")}, above the ₹${cashVoucherLimit.toLocaleString("en-IN")} limit.`
                        : `Cash vouchers for ${new Date(form.expense_date).toLocaleDateString("en-IN")} come to ₹${cashVoucherDayTotal.toLocaleString("en-IN")}, over the ₹${cashVoucherLimit.toLocaleString("en-IN")} daily limit.`}
                    </div>
                  )}
                  {form.bill_type === "cash_voucher" && !isVenueManager && !isCashVoucherOverLimit && cashVoucherBooked > 0 && (
                    <div style={{ marginTop:6, padding:"8px 12px", background:"#F1F5F9", borderRadius:8, fontSize:12, color:"#475569", fontWeight:600 }}>
                      ₹{cashVoucherBooked.toLocaleString("en-IN")} of cash vouchers already booked for {new Date(form.expense_date).toLocaleDateString("en-IN")} — ₹{cashVoucherRemaining.toLocaleString("en-IN")} left of the ₹{cashVoucherLimit.toLocaleString("en-IN")} daily limit.
                    </div>
                  )}
                </div>

                {/* Bill Type */}
                <div className="form-group">
                  <label className="form-label">Bill Type *</label>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                    {[
                      { value:"bill",         label:"📄 Bill/Receipt",  desc:"Original printed bill" },
                      { value:"cash_voucher", label:"✍️ Cash Voucher", desc:"Upload signed voucher photo" },
                    ].filter(opt => !isOetModuleCategory || form.category === "Catering Charges" || opt.value === "cash_voucher").map(opt => (
                      <div key={opt.value} onClick={() => {
                          setForm(f => ({...f, bill_type:opt.value, bill:null, cash_voucher_type:""}));
                          setBillPreview(null);
                          setCashBillWarning(false);
                          setCashApprovalSent(false);
                        }}
                        style={{
                          border:`2px solid ${form.bill_type===opt.value?"#E31E24":"#e5e7eb"}`,
                          borderRadius:10, padding:"10px 12px", cursor:"pointer",
                          background:form.bill_type===opt.value?"#FFEBEE":"white",
                          transition:"all 0.2s"
                        }}>
                        <div style={{ fontWeight:700, fontSize:13, color:form.bill_type===opt.value?"#E31E24":"#374151" }}>{opt.label}</div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{opt.desc}</div>
                      </div>
                    ))}
                  </div>
                  {isOetModuleCategory && form.category !== "Catering Charges" && (
                    <div style={{ marginTop:6, fontSize:12, color:"#64748b" }}>
                      OET staff payments are recorded through signed cash vouchers.
                    </div>
                  )}
                  {form.category === "Catering Charges" && (
                    <div style={{ marginTop:6, fontSize:12, color:"#475569" }}>
                      Upload either the original catering bill or a signed cash voucher. Every catering claim is sent to Adrian for review and approval.
                    </div>
                  )}
                </div>

                {/* Cash Voucher sub-type dropdown — ONLY for OET Expense Module categories */}
                {/* Cash Voucher TYPE sub-dropdown removed — no longer required for any category */}

                {/* Warning banner — the day's cash vouchers together exceed ₹500 */}
                {needsApproval && (
                  <div className="form-group">
                    <div style={{
                      padding:"14px 16px", background:"#FFF7ED",
                      border:"2px solid #FDE68A", borderRadius:12
                    }}>
                      <div style={{ fontSize:14, fontWeight:700, color:"#D97706", marginBottom:4 }}>
                        ⚠️ Manager Should Approve This Payment
                      </div>
                      <div style={{ fontSize:13, color:"#92400e" }}>
                        {oetCategoryLimit ? <>
                          {form.category} is <strong>₹{cashVoucherDayTotal.toLocaleString("en-IN")}</strong>, above the <strong>₹{cashVoucherLimit.toLocaleString("en-IN")}</strong> category limit.
                        </> : <>
                          Cash vouchers for {new Date(form.expense_date).toLocaleDateString("en-IN")} come to <strong>₹{cashVoucherDayTotal.toLocaleString("en-IN")}</strong>
                          {cashVoucherBooked > 0 && <> (₹{cashVoucherBooked.toLocaleString("en-IN")} already booked for the day)</>}, over the ₹{cashVoucherLimit.toLocaleString("en-IN")} daily limit.
                        </>} You can still submit — it will be marked <strong>Awaiting Approval</strong> and your Manager &amp; Finance team will be notified automatically.
                      </div>
                    </div>
                  </div>
                )}

                {/* Upload area — for all bill types (warning shown above if over limit) — hidden when shared-blocked */}
                {!isExpenseEntryBlocked && !isCompanyPaidEntry && (form.bill_type === "bill" || form.bill_type === "cash_voucher") && (
                  <div className="form-group">
                    <label className="form-label">
                      {form.bill_type === "cash_voucher" ? "Upload Signed Voucher Photo *"
                       : "Upload Bill Photo *"}
                    </label>

                    {/* ⚠️ Warning message */}
                    <div style={{
                      marginBottom:10, padding:"10px 14px", borderRadius:8,
                      background:"#FFF7ED", border:"1px solid #FDE68A",
                      fontSize:12, color:"#92400e",
                      display:"flex", alignItems:"flex-start", gap:8
                    }}>
                      <span style={{ fontSize:14 }}>⚠️</span>
                      <span><strong>Only original bills are accepted.</strong> UPI payment proofs will not be accepted.</span>
                    </div>

                    {billPreview ? (
                      <label className="upload-area" style={{ display:"block" }}>
                        <input type="file" accept="image/*,application/pdf" style={{ display:"none" }} onChange={handleFileChange} />
                        <img src={billPreview} alt="preview" style={{ maxHeight:150, maxWidth:"100%", borderRadius:8, objectFit:"contain" }} />
                      </label>
                    ) : (
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        <label className="upload-area" style={{ flex:"1 1 140px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", padding:"16px 8px" }}>
                          <input type="file" accept="image/*" capture="environment" style={{ display:"none" }} onChange={handleFileChange} />
                          <div style={{ fontSize:28, marginBottom:6 }}>📷</div>
                          <div style={{ fontWeight:600, color:"#374151", fontSize:13, textAlign:"center" }}>Take Photo</div>
                        </label>
                        <label className="upload-area" style={{ flex:"1 1 140px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", padding:"16px 8px" }}>
                          <input type="file" accept="image/*,application/pdf" style={{ display:"none" }} onChange={handleFileChange} />
                          <div style={{ fontSize:28, marginBottom:6 }}>🖼️</div>
                          <div style={{ fontWeight:600, color:"#374151", fontSize:13, textAlign:"center" }}>Choose from Gallery</div>
                        </label>
                        <div style={{ flexBasis:"100%", fontSize:12, color:"#9ca3af", marginTop:2 }}>JPG, PNG or PDF • PDFs auto-converted to image</div>
                      </div>
                    )}
                    {billPreview && (
                      <button type="button" className="btn btn-danger btn-sm" style={{ marginTop:8 }}
                        onClick={() => { setForm(f => ({...f, bill:null})); setBillPreview(null); }}>
                        Remove
                      </button>
                    )}
                  </div>
                )}

                {/* ── Shared Expense Section (Travel / Hotel / Food only) ─────────────── */}
                {["Travel","Hotel","Food"].includes(form.category) && !needsApproval && (() => {
                  // For Food the block message is shown above the food-type grid — show it here for Travel & Hotel
                  if (isSharedBlocked && form.category !== "Food") {
                    return (
                      <div style={{ padding:"12px 14px", background:"#FEF3C7", border:"1.5px solid #FDE68A", borderRadius:10, fontSize:13, color:"#92400e" }}>
                        🚫 You've already been included in a shared <strong>{form.category}</strong> expense on this date by <strong>{sharedBlockEntry.shared_by_name}</strong>. You cannot add a separate {form.category} expense for the same day.
                      </div>
                    );
                  }
                  if (isSharedBlocked) return null; // Food: message already shown in food-type section
                  return (
                    <div className="form-group">
                      <label className="form-label">Shared with Team Members?</label>
                      <div style={{ display:"flex", gap:10, marginBottom: form.is_shared ? 12 : 0 }}>
                        {[{v:false,label:"👤 For Myself Only"},{v:true,label:"👥 Shared"}].map(opt => (
                          <div key={String(opt.v)}
                            onClick={() => { setForm(f => ({
                              ...f, is_shared:opt.v, shared_with_ids:[],
                              food_external_groups: opt.v ? f.food_external_groups : {},
                              food_pack_count: opt.v ? f.food_pack_count : "",
                            })); setSharedSearch(""); }}
                            style={{
                              flex:1, padding:"10px 12px", borderRadius:10, cursor:"pointer",
                              border:`2px solid ${form.is_shared===opt.v?"#E31E24":"#e5e7eb"}`,
                              background:form.is_shared===opt.v?"#FFEBEE":"white",
                              textAlign:"center", fontSize:13, fontWeight:700,
                              color:form.is_shared===opt.v?"#E31E24":"#374151"
                            }}>
                            {opt.label}
                          </div>
                        ))}
                      </div>

                      {form.is_shared && (
                        <div style={{ marginTop:4 }}>
                          <div style={{ fontSize:12, color:"#6b7280", marginBottom:8 }}>
                            Select teammates who shared this {form.category.toLowerCase()} with you:
                          </div>
                          {companyEmployeesStatus === "loaded" && companyEmployees.length > 5 && (
                            <input
                              type="text"
                              value={sharedSearch}
                              onChange={e => setSharedSearch(e.target.value)}
                              placeholder="🔍 Search by name or email..."
                              style={{
                                width:"100%", boxSizing:"border-box", padding:"8px 12px", marginBottom:6,
                                border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, outline:"none",
                              }}
                            />
                          )}
                          <div style={{ maxHeight:240, overflowY:"auto", WebkitOverflowScrolling:"touch", touchAction:"pan-y", border:"1px solid #e5e7eb", borderRadius:8, padding:"4px 0" }}>
                            {companyEmployeesStatus === "loading" ? (
                              <div style={{ padding:"12px 14px", color:"#9ca3af", fontSize:13 }}>Loading employees...</div>
                            ) : companyEmployeesStatus === "error" ? (
                              <div style={{ padding:"12px 14px", color:"#dc2626", fontSize:13 }}>
                                Couldn't load employees.{" "}
                                <span
                                  style={{ textDecoration:"underline", cursor:"pointer" }}
                                  onClick={() => {
                                    setCompanyEmployeesStatus("loading");
                                    API.get("/expenses/company-employees")
                                      .then(r => { setCompanyEmployees(r.data); setCompanyEmployeesStatus("loaded"); })
                                      .catch(() => setCompanyEmployeesStatus("error"));
                                  }}
                                >Retry</span>
                              </div>
                            ) : companyEmployees.length === 0 ? (
                              <div style={{ padding:"12px 14px", color:"#9ca3af", fontSize:13 }}>No teammates found to share with.</div>
                            ) : companyEmployees.filter(emp => {
                              const q = sharedSearch.trim().toLowerCase();
                              if (!q) return true;
                              return emp.name?.toLowerCase().includes(q) || emp.email?.toLowerCase().includes(q);
                            }).length === 0 ? (
                              <div style={{ padding:"12px 14px", color:"#9ca3af", fontSize:13 }}>No teammates match "{sharedSearch}".</div>
                            ) : companyEmployees.filter(emp => {
                              const q = sharedSearch.trim().toLowerCase();
                              if (!q) return true;
                              return emp.name?.toLowerCase().includes(q) || emp.email?.toLowerCase().includes(q);
                            }).map(emp => {
                              const selected = form.shared_with_ids.includes(emp.id);
                              return (
                                <div key={emp.id}
                                  onClick={() => setForm(f => ({
                                    ...f,
                                    shared_with_ids: selected
                                      ? f.shared_with_ids.filter(x => x !== emp.id)
                                      : [...f.shared_with_ids, emp.id]
                                  }))}
                                  style={{
                                    display:"flex", alignItems:"center", gap:10,
                                    padding:"9px 14px", cursor:"pointer",
                                    background:selected?"#FFEBEE":"transparent",
                                    borderBottom:"1px solid #f3f4f6"
                                  }}>
                                  <div style={{
                                    width:18, height:18, borderRadius:4, flexShrink:0,
                                    border:`2px solid ${selected?"#E31E24":"#d1d5db"}`,
                                    background:selected?"#E31E24":"white",
                                    display:"flex", alignItems:"center", justifyContent:"center"
                                  }}>
                                    {selected && <span style={{ color:"white", fontSize:11, fontWeight:800 }}>✓</span>}
                                  </div>
                                  <div>
                                    <div style={{ fontSize:13, fontWeight:600, color:"#1f2937" }}>{emp.name}</div>
                                    <div style={{ fontSize:11, color:"#9ca3af" }}>{emp.email}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {form.category === "Food" && (
                            <div style={{ marginTop:12, padding:12, border:"1px solid #dbeafe", borderRadius:10, background:"#f8fbff" }}>
                              <div style={{ fontSize:13, fontWeight:700, color:"#1f2937", marginBottom:4 }}>External / third-party people</div>
                              <div style={{ fontSize:12, color:"#64748b", marginBottom:10 }}>Names are not required. Enter a count for every group served.</div>
                              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:8 }}>
                                {FOOD_EXTERNAL_GROUP_TYPES.map(type => (
                                  <label key={type} style={{ fontSize:12, color:"#374151", fontWeight:600 }}>
                                    {type}
                                    <input type="number" min="0" step="1" inputMode="numeric"
                                      value={form.food_external_groups?.[type] || ""}
                                      onChange={event => setForm(current => ({
                                        ...current,
                                        food_external_groups: { ...current.food_external_groups, [type]: event.target.value },
                                      }))}
                                      placeholder="People count"
                                      style={{ width:"100%", boxSizing:"border-box", marginTop:4, padding:"8px 10px", border:"1px solid #cbd5e1", borderRadius:7 }} />
                                  </label>
                                ))}
                              </div>
                              <label style={{ display:"block", marginTop:10, fontSize:12, color:"#374151", fontWeight:700 }}>
                                Total food packs *
                                <input type="number" min="1" step="1" inputMode="numeric"
                                  value={form.food_pack_count}
                                  onChange={event => setForm(current => ({ ...current, food_pack_count:event.target.value }))}
                                  placeholder="Number of food packs"
                                  style={{ width:"100%", boxSizing:"border-box", marginTop:4, padding:"9px 10px", border:"1px solid #cbd5e1", borderRadius:7 }} />
                              </label>
                            </div>
                          )}

                          {(form.shared_with_ids.length > 0 || Object.values(form.food_external_groups || {}).some(value => Number(value) > 0)) && (() => {
                            const externalCount = form.category === "Food"
                              ? Object.values(form.food_external_groups || {}).reduce((sum, value) => sum + (Number(value) || 0), 0)
                              : 0;
                            const peopleCount = 1 + form.shared_with_ids.length + externalCount;
                            return (
                            <div style={{ marginTop:8, padding:"8px 12px", background:"#D1FAE5", borderRadius:8, fontSize:12, color:"#065F46", fontWeight:600 }}>
                              💡 {form.category} ₹{parseFloat(form.amount||0).toLocaleString("en-IN")} ÷ {peopleCount} people
                              = <strong>₹{(parseFloat(form.amount||0) / peopleCount).toLocaleString("en-IN", {minimumFractionDigits:2})}</strong> per person
                              <div style={{ fontWeight:400, marginTop:2 }}>
                                The full expense remains under your trip. The equal split is used for analytics only.
                              </div>
                            </div>
                          );})()}
                        </div>
                      )}
                    </div>
                  );
                })()}

                  </>
                )}
              </div>
              <div className="modal-footer">
                <span style={{marginRight:"auto",color:"#64748b",fontSize:11}}>{draftSaved ? "Draft saved. Future you says thanks." : ""}</span>
                <button type="button" className="btn btn-secondary" onClick={closeExpenseModal}>Close</button>
                <button
                  type="button"
                  onClick={() => setShowExpensePreview(true)}
                  disabled={isExpenseEntryBlocked}
                  title={isExpenseEntryBlocked ? "This expense is blocked" : ""}
                  style={{
                    padding:"8px 18px", borderRadius:8, fontWeight:700, fontSize:13,
                    cursor: isExpenseEntryBlocked ? "not-allowed" : "pointer",
                    border:"1.5px solid #E31E24", color: isExpenseEntryBlocked ? "#9ca3af" : "#E31E24",
                    background:"transparent", opacity: isExpenseEntryBlocked ? 0.5 : 1,
                  }}>
                  👁️ Preview
                </button>
                <button type="submit" className="btn btn-primary"
                  disabled={saving || isExpenseEntryBlocked}
                  title={isExpenseEntryBlocked ? "This expense is blocked" : ""}>
                  {saving ? "Saving..." : editingExpense ? "✅ Save Changes" : needsApproval ? "✅ Submit for Approval" : "✅ Add Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ Expense Preview Modal ══ */}
      {showExpensePreview && (
        <div className="modal-overlay" onClick={() => setShowExpensePreview(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background:"#1f2937" }}>
              <h2 style={{ fontSize:17, fontWeight:700, color:"#f9fafb" }}>👁️ Expense Preview</h2>
              <button onClick={() => setShowExpensePreview(false)}
                style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>

            <div className="modal-body" style={{ padding:"20px 24px", background:"#f9fafb" }}>

              {/* Main expense card */}
              <div style={{
                background:"white", borderRadius:12, overflow:"hidden",
                border:"1px solid #e5e7eb", boxShadow:"0 2px 8px rgba(0,0,0,0.06)"
              }}>
                {/* Category header strip */}
                <div style={{
                  padding:"14px 18px",
                  background:"linear-gradient(135deg,#E31E24,#B71C1C)",
                  display:"flex", alignItems:"center", gap:12
                }}>
                  <span style={{ fontSize:28 }}>{CATEGORY_ICONS[form.category] || "📌"}</span>
                  <div>
                    <div style={{ fontSize:16, fontWeight:800, color:"white" }}>{form.category}</div>
                    {form.travel_subcategory && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:1 }}>🚌 {form.travel_subcategory}</div>
                    )}
                    {form.food_subcategory && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:1 }}>{form.food_subcategory}</div>
                    )}
                    {form.hotel_name && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:1 }}>🏨 {form.hotel_name}</div>
                    )}
                    {form.courier_subtype && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:1 }}>📦 {form.courier_subtype}{form.courier_name ? ` · ${form.courier_name}` : ""}{form.courier_weight ? ` · ${form.courier_weight} kg` : ""}</div>
                    )}
                    {form.print_subcategory && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:1 }}>🖨️ {form.print_subcategory}</div>
                    )}
                    {form.misc_category && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:1 }}>📌 {form.misc_category}</div>
                    )}
                    {form.cash_voucher_type && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:1 }}>✍️ {form.cash_voucher_type}</div>
                    )}
                  </div>
                  <div style={{ marginLeft:"auto", textAlign:"right" }}>
                    <div style={{ fontSize:22, fontWeight:900, color:"white" }}>
                      ₹{parseFloat(form.amount || 0).toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.75)", marginTop:1 }}>
                      📅 {form.expense_date
                        ? new Date(form.expense_date).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })
                        : "—"}
                    </div>
                  </div>
                </div>

                {/* Details section */}
                <div style={{ padding:"16px 18px" }}>
                  {/* Description */}
                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".04em", marginBottom:3 }}>Description</div>
                    <div style={{ fontSize:14, color:"#374151", fontStyle: form.description ? "normal" : "italic" }}>
                      {form.description || <span style={{ color:"#d1d5db" }}>No description</span>}
                    </div>
                  </div>

                  {/* Bill type + badges */}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12 }}>
                    <span style={{
                      fontSize:12, padding:"4px 12px", borderRadius:20, fontWeight:700,
                      background: form.bill_type==="cash_voucher" ? "#FEF3C7" : "#D1FAE5",
                      color:      form.bill_type==="cash_voucher" ? "#D97706" : "#065F46",
                    }}>
                      {form.bill_type === "cash_voucher" ? "✍️ Cash Voucher" : "📄 Bill/Receipt"}
                    </span>
                    {form.breakfast_included && (
                      <span style={{ fontSize:12, padding:"4px 12px", borderRadius:20, fontWeight:700, background:"#D1FAE5", color:"#065F46" }}>🍳 BF Included</span>
                    )}
                    {form.hotel_company_paid && (
                      <span style={{ fontSize:12, padding:"4px 12px", borderRadius:20, fontWeight:700, background:"#DBEAFE", color:"#1e40af" }}>🏢 Company Paid</span>
                    )}
                    {form.bf_override_reason && (
                      <span style={{ fontSize:12, padding:"4px 12px", borderRadius:20, fontWeight:700, background:"#FEF3C7", color:"#92400e" }}>⚠️ BF Override</span>
                    )}
                    {form.is_shared && form.shared_with_ids.length > 0 && (
                      <span style={{ fontSize:12, padding:"4px 12px", borderRadius:20, fontWeight:700, background:"#EDE9FE", color:"#5b21b6" }}>
                        👥 Shared with {form.shared_with_ids.length} colleague{form.shared_with_ids.length > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  {/* Shared split info */}
                  {form.is_shared && (form.shared_with_ids.length > 0 || (form.category === "Food" && Object.values(form.food_external_groups || {}).some(value => Number(value) > 0))) && (() => {
                    const externalCount = form.category === "Food"
                      ? Object.values(form.food_external_groups || {}).reduce((sum, value) => sum + (Number(value) || 0), 0)
                      : 0;
                    const peopleCount = 1 + form.shared_with_ids.length + externalCount;
                    return (
                    <div style={{ marginBottom:12, padding:"8px 12px", background:"#EDE9FE", borderRadius:8, fontSize:12, color:"#5b21b6" }}>
                      💡 ₹{parseFloat(form.amount||0).toLocaleString("en-IN")} ÷ {peopleCount} people = <strong>₹{(parseFloat(form.amount||0)/peopleCount).toLocaleString("en-IN",{minimumFractionDigits:2})}</strong> per person (Food analytics only)
                      <div style={{ marginTop:3, color:"#7c3aed" }}>
                        Shared with: {form.shared_with_ids.map(sid => companyEmployees.find(e=>e.id===sid)?.name).filter(Boolean).join(", ")}
                        {externalCount > 0 && `${form.shared_with_ids.length ? " · " : ""}${externalCount} external people`}
                      </div>
                    </div>
                  );})()}

                  {/* Bill preview image */}
                  {billPreview && (
                    <div style={{ marginTop:4 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".04em", marginBottom:6 }}>Bill Photo</div>
                      <img
                        src={billPreview}
                        alt="Bill preview"
                        style={{
                          width:"100%", maxHeight:220, objectFit:"contain",
                          borderRadius:8, border:"1px solid #e5e7eb", background:"#f9fafb"
                        }}
                      />
                    </div>
                  )}

                  {/* Missing bill / voucher warning */}
                  {(form.bill_type === "bill" || form.bill_type === "cash_voucher") && !billPreview && (
                    <div style={{ marginTop:4, padding:"8px 12px", background:"#FEE2E2", borderRadius:8, fontSize:12, color:"#DC2626", fontWeight:600 }}>
                      ⚠️ {form.bill_type === "cash_voucher" ? "No voucher photo uploaded yet — go back and upload the signed voucher." : "No bill photo uploaded yet — go back and upload one before submitting."}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop:14, fontSize:12, color:"#9ca3af", textAlign:"center" }}>
                Review the details above. Go back to edit, or confirm to add this expense.
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExpensePreview(false)}>
                ← Edit
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || (!needsApproval && (form.bill_type === "bill" || form.bill_type === "cash_voucher") && !billPreview)}
                title={((form.bill_type === "bill" || form.bill_type === "cash_voucher") && !billPreview) ? "Upload the bill/voucher photo first" : ""}
                onClick={() => {
                  setShowExpensePreview(false);
                  // Programmatically submit the form
                  document.getElementById("add-expense-form")?.requestSubmit();
                }}>
                {saving ? "Saving..." : "✅ Confirm & Add Expense"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ BF Override Confirmation Modal ══ */}
      {showBfModal && (
        <div className="modal-overlay" onClick={() => setShowBfModal(false)}>
          <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize:16, fontWeight:700, color:"#92400e" }}>⚠️ Breakfast Override</h2>
              <button onClick={() => setShowBfModal(false)} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ padding:"12px 16px", background:"#FEF3C7", borderRadius:10, marginBottom:16, fontSize:14, color:"#78350f" }}>
                Your hotel bill includes breakfast. Adding a separate BF expense requires manager approval.
              </div>
              <div className="form-group">
                <label className="form-label">Reason for Override *</label>
                <select className="form-control" value={pendingBfOverrideReason} onChange={e => setPendingBfOverrideReason(e.target.value)}>
                  <option value="">— Select reason —</option>
                  {BF_OVERRIDE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>
                Your manager and finance team will be notified of this override request.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowBfModal(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!pendingBfOverrideReason || saving}
                onClick={handleConfirmBfOverride}
                style={{ background:"#D97706" }}>
                {saving ? "Submitting..." : "Submit with Override"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ask Query Modal (manager/admin only) ─────────────────────────── */}
      {tdQueryModal && (
        <div style={{ position:"fixed", inset:0, zIndex:9100, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={() => { setTdQueryModal(null); setTdQueryText(""); }}>
          <div style={{ background:"#fff", borderRadius:16, padding:28, maxWidth:500, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:16, fontWeight:800, color:"#1f2937", marginBottom:4 }}>❓ Ask Query about Expense</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:16 }}>{tdQueryModal.label}</div>
            <textarea
              autoFocus
              rows={4}
              placeholder="e.g. Please provide the hotel invoice for this entry. The amount seems higher than usual for this destination."
              value={tdQueryText}
              onChange={e => setTdQueryText(e.target.value)}
              style={{ width:"100%", borderRadius:8, border:"1.5px solid #d1d5db", padding:"10px 12px", fontSize:13, fontFamily:"inherit", resize:"vertical", outline:"none", boxSizing:"border-box", marginBottom:16 }}
              onFocus={e => e.target.style.borderColor = "#6366f1"}
              onBlur={e => e.target.style.borderColor = "#d1d5db"}
            />
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => { setTdQueryModal(null); setTdQueryText(""); }}
                className="btn btn-secondary" type="button">
                Cancel
              </button>
              <button onClick={handleTdAskQuery} disabled={tdQueryBusy || !tdQueryText.trim()}
                className="btn btn-primary" type="button"
                style={{ background:"#6366f1", opacity: !tdQueryText.trim() || tdQueryBusy ? 0.6 : 1 }}>
                {tdQueryBusy ? "Sending..." : "Send Query 📩"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
